import {
  PlayExperience,
  playExperiences,
  experiencePath,
  resolvePlayEntry,
  MATCHMAKING_ENABLED,
  MATCHMAKING_UNAVAILABLE,
} from './dashboard/playExperiences'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import useTheme from '@mui/material/styles/useTheme'
import { err, isDefined, wait } from '../utils/functions'
import AppContext from '../app/AppContext'
import { awardMultiplayerSoloXp, getSoloStats, getUser, logout } from '../api/user'
import Cookies from 'js-cookie'
import { AlertInput } from '../components/common/Alert'
import {
  createList,
  getDailyList,
  getList,
  getRandomList,
  playedList,
  rateList,
} from '../api/lists'
import {
  getDailyPoll,
  getPoll,
  getRandomPoll,
  playedPoll,
  startQuestionGame,
  finishQuestionLocal,
} from '../api/polls'
import { List as ListType } from '../api/types/lists'
import { Game } from '../api/types/games'
import { getGames } from '../api/games'
import { getMatchmakingLeaderboard, getMyMatchmakingStats } from '../api/matchmaking'
import DashboardShell from 'src/components/dashboard/views/DashboardShell'
import { Capacitor } from '@capacitor/core'
import useGameAudio from 'src/ui/sound/useGameAudio'
import { Poll } from 'src/api/types/polls'
import DashboardContentView from 'src/components/dashboard/views/DashboardContentView'
import usePollState from 'src/pages/hooks/usePollState'
import { Item } from '@components/common/ItemGuessBox'
import { sortAlpha, sortIndex, toSoloItems } from 'src/pages/dashboard/listUtils'
import { MatchmakingStats } from 'src/api/types/matchmaking'
import { UserSoloStats } from 'src/api/types/users'
import { clearAuthStorage, getAuthToken } from 'src/utils/helpers/authStorage'

export const isNative = Capacitor.isNativePlatform()
export const isIOS = Capacitor.getPlatform() === 'ios'

const socketUrl =
  import.meta.env.MODE === 'development'
    ? 'ws://localhost:4040/ws'
    : `wss://www.ranktrivia.com/ws`
const ACTIVE_LOBBY_CODE_KEY = 'ranktrivia_active_lobby_code'
const ACTIVE_LOBBY_PLAYER_SESSION_KEY = 'ranktrivia_active_lobby_player_session'
const ACTIVE_LOBBY_ANON_ID_KEY = 'ranktrivia_active_lobby_anon_id'
const ACTIVE_LOBBY_MATCH_TYPE_KEY = 'ranktrivia_active_lobby_match_type'
const ACTIVE_LOBBY_GAME_STARTED_KEY = 'ranktrivia_active_lobby_game_started'

function createAnonId(): string {
  return `anon_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
}

function getOrCreateAnonId(): string {
  if (typeof window === 'undefined') return createAnonId()
  try {
    const existing = (localStorage.getItem(ACTIVE_LOBBY_ANON_ID_KEY) || '').trim()
    if (existing) return existing
    const created = createAnonId()
    localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, created)
    return created
  } catch {
    return createAnonId()
  }
}

export type Player = {
  id?: string
  playerSessionId?: string
  userId?: number
  anonId?: string
  name: string
  team?: number
  icon?: string
}

export type DailyListResult = {
  correct: number
  total: number
  guesses: number
  completionMs?: number
  result: 'win' | 'loss'
  rank?: number
  totalParticipants?: number
  percentile?: number
  topPercentLabel?: string
}

export type DailyProgressState = {
  listId: number
  startedAtMs: number
  updatedAt: number
  resumeState: {
    guessItems: Array<{
      id: string
      title: string
      metric?: string
      index: number
      points: number
      locked?: boolean
      status?: 'correct' | 'unchecked'
      justAdded?: boolean
    }>
    guessCount: number
    guessHistoryById: Record<string, number[]>
    lastGuessCorrectById: Record<string, boolean>
    lastGuessOrder: string[]
    result: 'active' | 'win' | 'loss'
  }
}

export type OnlineVoteState = {
  code: string
  team: number
  deadlineAtMs: number
  votes: Array<{ connectionId: string; selectedIndex: number }>
}

export type OnlineRematchState = {
  code: string
  deadlineAtMs: number
  voters: string[]
}

export type ViewMode =
  | 'home'
  | 'solo'
  | 'multi'
  | 'host'
  | 'poll_detail'
  | 'poll_vote'
  | 'poll_play'
  | 'list_edit'

// const ONLINE_DISABLED = true

export default function Dashboard(): JSX.Element {
  const sanitizeLobbyCode = React.useCallback((value: string): string => {
    return value
      .trim()
      .toUpperCase()
      .replace(/^#+/, '')
      .replace(/[^A-Z0-9]/g, '')
  }, [])

  const theme = useTheme()
  const history = useHistory()
  const params = useParams<{
    mode?: string
    listId?: string
    pollId?: string
    pollAction?: string
  }>()

  const location = useLocation()
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const listIdFromQuery = searchParams.get('l')
  const listIdFromPath = params.listId
    ? Number.parseInt(String(params.listId), 10)
    : undefined
  const listIdFromPathValid =
    typeof listIdFromPath === 'number' &&
    Number.isFinite(listIdFromPath) &&
    listIdFromPath > 0
  const routeListId = listIdFromPathValid
    ? listIdFromPath
    : listIdFromQuery
      ? Number.parseInt(String(listIdFromQuery), 10)
      : undefined
  const listId =
    typeof routeListId === 'number' && Number.isFinite(routeListId) && routeListId > 0
      ? routeListId
      : undefined
  const pollIdParam = searchParams.get('poll') ?? searchParams.get('p')
  const joinCodeParam = sanitizeLobbyCode(searchParams.get('join') || '')

  const { appState, appReducer } = React.useContext(AppContext)

  const user = appState.user

  const [saving, setSaving] = useState(false)

  const [topic, setTopic] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [pollLoading, setPollLoading] = useState<boolean>(false)
  const pollLoadingSinceRef = useRef<number | null>(null)
  const [selectedList, setSelectedList] = useState<number | undefined>()
  const [listRated, setListRated] = useState<number | undefined>()
  const [activePollId, setActivePollId] = useState<number | undefined>()
  const [listItems, setListItems] = useState<Item[]>([])
  const orderedListItems = useMemo(() => {
    const guessed = listItems.filter((l) => l.team).sort((a, b) => a.index - b.index)
    const remaining = listItems.filter((l) => !l.team)
    return guessed.concat(remaining)
  }, [listItems])
  const pendingStartItemsRef = useRef<{
    items: Item[]
    topic: string
    listId?: number
  } | null>(null)
  const startPollGameRef = useRef<
    (poll: Poll, options?: { silentStart?: boolean }) => void
  >(() => {
    //
  })

  const SINGLE_PLAY_ITEMS = 10

  const [players, setPlayers] = useState<Player[]>([
    {
      name: user?.name || 'default',
      team: 1,
      id: user?.id ? `${user?.id}` : undefined,
    },
  ])
  const [onlineProfileName, setOnlineProfileName] = useState<string>(user?.name || '')
  const [onlineProfileIcon, setOnlineProfileIcon] = useState<string>('🙂')
  const [myPlayer, setMyPlayer] = useState<Player | undefined>()

  const [dailyList, setDailyList] = useState<ListType | undefined>()
  const [dailyResult, setDailyResult] = useState<DailyListResult | undefined>(undefined)
  const [dailyProgress, setDailyProgress] = useState<DailyProgressState | undefined>(
    undefined
  )
  const [soloStats, setSoloStats] = useState<UserSoloStats | undefined>(undefined)
  const [soloXpAwarded, setSoloXpAwarded] = useState<number | undefined>(undefined)
  const [editListDraft, setEditListDraft] = useState<ListType | undefined>(undefined)

  const [myGames, setMyGames] = useState<Game[]>([])

  const [gameState, setGameState] = useState<string>('pre')
  const [selectedTab, setSelectedTab] = useState<string>('play')
  const [selectedListTab, setSelectedListTab] = useState<string>('daily') // random find generate polls
  const [listSearchShowMine, setListSearchShowMine] = useState<boolean>(false)

  const [viewMode, setViewMode] = useState<ViewMode>('home')
  const [pollBackToRandom, setPollBackToRandom] = useState<boolean>(false)
  const pollBackOriginRef = useRef<'random' | 'polls' | null>(null)
  const [pollPlayBackTarget, setPollPlayBackTarget] = useState<'polls' | 'detail'>(
    'polls'
  )
  const [dailyPollContext, setDailyPollContext] = useState<
    'today' | 'previous' | undefined
  >()
  const initialShowModeSelectOnly = useMemo(() => {
    if (params.mode) return false
    if (params.pollId) return false
    if (params.listId) return false
    if (listId) return false
    if (pollIdParam) return false
    return true
  }, [params.mode, params.pollId, params.listId, listId, pollIdParam])
  const [showModeSelectOnly, setShowModeSelectOnly] = useState<boolean>(
    initialShowModeSelectOnly
  )
  const canCreatePoll = Boolean(appState?.user?.id)
  const isPollPlayView = viewMode === 'poll_play'
  const [booted, setBooted] = useState<boolean>(false)
  const bootedRef = useRef<boolean>(false)
  const finalizeBoot = () => {
    if (bootedRef.current) return
    bootedRef.current = true
    setBooted(true)
  }

  const [experience, updateExperience] = useState<PlayExperience>()
  const experienceRef = useRef<PlayExperience>()
  const setExperience = (value?: PlayExperience) => {
    experienceRef.current = value
    updateExperience(value)
  }
  const exitExperienceRef = useRef<() => boolean>(() => true)
  const exitedLobbyCodesRef = useRef(new Set<string>())
  const contentEpochRef = useRef(0)
  const renderContentEpoch = contentEpochRef.current
  const homePath = experiencePath(experience)
  const [mode, setMode] = useState<'single' | 'multi' | 'host' | undefined>(undefined)
  const [hostPlayMode, setHostPlayMode] = useState<string>('in-order')
  const [multiMode, setMultiMode] = useState<string>('local')
  const randomListRequestRef = useRef(0)
  const [soloGameKey, setSoloGameKey] = useState<number>(0)
  const [joinGameCode, setJoinGameCode] = useState<string>('')
  const [showEmojiPreview, setShowEmojiPreview] = useState<boolean>(false)
  const [selectedPlayer, setSelectedPlayer] = useState<Player | undefined>(undefined)
  const [hostId, setHostId] = useState<string>('')
  const [gameCode, setGameCode] = useState<string>('')
  const randomListContext = [
    experience,
    mode,
    multiMode,
    gameCode,
    selectedListTab,
    location.pathname,
  ].join('|')
  const randomListContextRef = useRef(randomListContext)
  randomListContextRef.current = randomListContext
  const [volumeLevel, setVolumeLevel] = useState<number>(50)
  const [audioEnabled, setAudioEnabled] = useState<boolean>(true)
  const [hasAnyGuess, setHasAnyGuess] = useState<boolean>(false)
  const [howToPlayOpen, setHowToPlayOpen] = useState<boolean>(false)
  const [ws, setWs] = useState<WebSocket>()
  const [wsAuthenticated, setWsAuthenticated] = useState<boolean>(false)
  const [rateAlert, setRateAlert] = useState<AlertInput>({ open: false })
  const [listAlert, setListAlert] = useState<AlertInput>({ open: false })
  // const [reconnectTimeout, setReconnectTimeout] = useState<NodeJS.Timeout | undefined>(undefined)
  // const [wsOpen, setWsOpen] = useState<boolean>(false)
  const [connectionId, setConnectionId] = useState<string>('')
  const [onlineUsersAll, setOnlineUsersAll] = useState<number>(0)
  const [onlineUsersLogged, setOnlineUsersLogged] = useState<number>(0)
  const [searchingRanked, setSearchingRanked] = useState<number>(0)
  const [searchingUnranked, setSearchingUnranked] = useState<number>(0)
  const [matchmakingRated, setMatchmakingRated] = useState<boolean>(!!appState?.user?.id)
  const [matchmakingLeaderboard, setMatchmakingLeaderboard] = useState<
    MatchmakingStats[]
  >([])
  const [myMatchmakingStats, setMyMatchmakingStats] = useState<
    MatchmakingStats | undefined
  >()
  const [onlineMatchType, setOnlineMatchType] = useState<
    'matchmaking' | 'lobby' | undefined
  >()
  const [onlineMatchRated, setOnlineMatchRated] = useState<boolean | undefined>()
  const [onlineMatchTrophyDelta, setOnlineMatchTrophyDelta] = useState<
    number | undefined
  >()
  const [multiplayerXpAwarded, setMultiplayerXpAwarded] = useState<number | undefined>()
  const [pendingMatch, setPendingMatch] = useState<boolean>(false)
  const [onlinePlayerSessionId, setOnlinePlayerSessionId] = useState<string>(
    localStorage.getItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY) || ''
  )
  const [onlineAnonId, setOnlineAnonId] = useState<string>(() => getOrCreateAnonId())
  const [authoritativeOnlineAction, setAuthoritativeOnlineAction] = useState<
    | {
        nonce: number
        selectedIndex: number
        result?: 'hit' | 'miss'
        actingTeam?: number
        teamTurn?: number
        teamOneScore?: number
        teamTwoScore?: number
      }
    | undefined
  >(undefined)
  const [onlineVoteState, setOnlineVoteState] = useState<OnlineVoteState | undefined>(
    undefined
  )
  const [onlineRematchState, setOnlineRematchState] = useState<
    OnlineRematchState | undefined
  >(undefined)
  const attemptedAutoJoinRef = useRef<string>('')
  const reconnectJoinSocketRef = useRef<WebSocket | undefined>(undefined)
  const pendingMatchRef = useRef<boolean>(false)
  const selectedStartRetryRef = useRef<{ listId?: number; attempts: number }>({
    attempts: 0,
  })
  const onlineMatchTypeRef = useRef<'matchmaking' | 'lobby' | undefined>(undefined)
  const connectionIdRef = useRef<string>('')
  const currentUserIdRef = useRef<number | undefined>(appState?.user?.id)
  const onlineAnonIdRef = useRef<string>(onlineAnonId)
  const handleRestartGameRef = useRef(() => {
    //
  })

  useEffect(() => {
    pendingMatchRef.current = pendingMatch
  }, [pendingMatch])

  useEffect(() => {
    onlineMatchTypeRef.current = onlineMatchType
  }, [onlineMatchType])

  useEffect(() => {
    connectionIdRef.current = connectionId
  }, [connectionId])

  useEffect(() => {
    currentUserIdRef.current = appState?.user?.id
  }, [appState?.user?.id])

  useEffect(() => {
    if (!appState?.user?.id) {
      setSoloStats(undefined)
      setSoloXpAwarded(undefined)
      return
    }
    getSoloStats()
      .then((res) => {
        if (res.success && res.data) {
          setSoloStats(res.data)
        }
      })
      .catch((e) => err(e))
  }, [appState?.user?.id])

  useEffect(() => {
    onlineAnonIdRef.current = onlineAnonId
  }, [onlineAnonId])
  const handleCloseLobbyRef = useRef(() => {
    //
  })

  const clearStaleLobbyState = () => {
    localStorage.removeItem(ACTIVE_LOBBY_CODE_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_MATCH_TYPE_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_GAME_STARTED_KEY)
    attemptedAutoJoinRef.current = ''
    reconnectJoinSocketRef.current = undefined
    setGameCode('')
    setJoinGameCode('')
    setHostId('')
    setPlayers([])
    setPendingMatch(false)
    setOnlineMatchType(undefined)
    setOnlineMatchRated(undefined)
    setOnlineMatchTrophyDelta(undefined)
    setMultiplayerXpAwarded(undefined)
    setOnlinePlayerSessionId('')
    setOnlineVoteState(undefined)
    setOnlineRematchState(undefined)
    setAuthoritativeOnlineAction(undefined)
  }

  const resetMatchmakingSessionState = () => {
    localStorage.removeItem(ACTIVE_LOBBY_CODE_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_MATCH_TYPE_KEY)
    localStorage.removeItem(ACTIVE_LOBBY_GAME_STARTED_KEY)
    setOnlinePlayerSessionId('')
    setGameCode('')
    setJoinGameCode('')
    setPlayers([])
    setPendingMatch(false)
    setOnlineMatchType(undefined)
    setOnlineMatchRated(undefined)
    setOnlineMatchTrophyDelta(undefined)
    setMultiplayerXpAwarded(undefined)
    setTopic('')
    setListItems([])
    setOnlineVoteState(undefined)
    setOnlineRematchState(undefined)
    setAuthoritativeOnlineAction(undefined)
    setGameState('pre')
    setSelectedTab('play')
    setSelectedListTab('online')
    setViewMode('home')
    setMode('multi')
    setMultiMode('online')
    if (!MATCHMAKING_ENABLED) {
      setExperience(undefined)
      setMode(undefined)
      setShowModeSelectOnly(true)
      history.replace('/')
    }
  }

  const normalizeAndShuffleOnlineItems = React.useCallback((input: unknown): Item[] => {
    if (!Array.isArray(input)) return []
    const next = input
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((raw: any, i: number) => ({
        question: raw?.question,
        title: String(raw?.title || ''),
        metric: raw?.metric ? String(raw.metric) : undefined,
        index: Number(raw?.index || i + 1),
        points: typeof raw?.points === 'number' ? Number(raw.points) : 1,
        team: raw?.team === 1 || raw?.team === 2 ? Number(raw.team) : undefined,
        guessed: Boolean(raw?.guessed),
      }))
      .filter((item) => item.title.trim().length > 0)
    for (let i = next.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      const a = next[i]
      const b = next[j]
      if (!a || !b) continue
      next[i] = b
      next[j] = a
    }
    return next
  }, [])

  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  )
  const [isAdminUser, setIsAdminUser] = useState(false)
  const open = Boolean(avatarMenuAnchorEl)
  const handleAvatarMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    playClick1()
    setAvatarMenuAnchorEl(event.currentTarget)
  }
  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchorEl(null)
  }

  useEffect(() => {
    const userId = appState.user?.id
    if (!userId) {
      setIsAdminUser(false)
      return
    }
    getUser(userId)
      .then((res) => {
        const role = res.data?.role
        setIsAdminUser(role === 'admin' || role === 'superadmin')
      })
      .catch(() => setIsAdminUser(false))
  }, [appState.user?.id])

  // useEffect(() => {
  //   if (gameState === 'active') {
  //     scrollToTop('smooth')
  //   }
  // }, [gameState])

  const viewVariants = {
    enter: { opacity: 0, y: 12, scale: 0.99 },
    center: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.1, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      y: -8,
      scale: 0.99,
      transition: { duration: 0.1, ease: 'easeIn' },
    },
  }

  useEffect(() => {
    if (!listId) return
    let canceled = false
    const epoch = contentEpochRef.current
    async function init(nextListId: number) {
      setLoading(true)
      const res = await getList(nextListId)
      if (canceled || epoch !== contentEpochRef.current) return
      if (res.success && res.data) {
        const selectedList = res.data
        const mappedItems =
          selectedList.items?.map((item, i) => ({
            title: item.title,
            metric: item.metric,
            index: item.index || item.rank || i + 1,
            points: 1,
            listId: selectedList.id || 0,
          })) || []
        if (!experienceRef.current) setExperience('solo')
        const engineMode = playExperiences[experienceRef.current || 'solo'].mode
        setMode(engineMode)
        setViewMode(engineMode === 'single' ? 'solo' : 'multi')
        setShowModeSelectOnly(false)
        setSelectedList(selectedList.id)

        setActivePollId(undefined)
        setTopic(selectedList.prompt || '')
        pendingStartItemsRef.current = {
          listId: selectedList.id,
          topic: selectedList.prompt || '',
          items: mappedItems,
        }
        setListItems(
          engineMode === 'single'
            ? toSoloItems(mappedItems, SINGLE_PLAY_ITEMS)
            : engineMode === 'host'
              ? sortIndex(mappedItems)
              : mappedItems
        )
        setSelectedTab('play')
        setLoading(false)
        return true
      }
      setLoading(false)

      // start your singlePlayer game with that list
      // startSinglePlayerGame({ listId: Number(listId) });

      // store listData in state, etc…
    }

    finalizeBoot()
    init(listId)
      .then((loaded) => {
        if (!loaded || canceled || epoch !== contentEpochRef.current) return
        if (!['lobby', 'online'].includes(experienceRef.current || '')) playStart()
        handleStartGame(listId, false)
      })
      .catch((e) => err(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      canceled = true
    }
  }, [listId])

  useEffect(() => {
    if (!pollIdParam) return
    let canceled = false
    const epoch = contentEpochRef.current
    const pollId = Number(pollIdParam)
    if (!pollId || Number.isNaN(pollId)) return
    const guestKeyStorage = `poll_guest_key_${pollId}`
    const localKey = user?.id
      ? undefined
      : localStorage.getItem(guestKeyStorage) || undefined
    if (pollBackOriginRef.current !== 'random') {
      setPollBackToRandom(false)
      pollBackOriginRef.current = 'polls'
    }
    setSelectedListTab('polls')
    pollState.setPollId(pollId)
    if (!experienceRef.current) setExperience('solo')
    setMode((current) => current || 'single')
    setViewMode('poll_vote')
    setShowModeSelectOnly(false)
    finalizeBoot()
    getPoll(pollId, localKey ? { localKey } : undefined)
      .then((res) => {
        if (canceled || epoch !== contentEpochRef.current) return
        const hasVoted = !!res.data?.viewerSubmitted
        setViewMode(hasVoted ? 'poll_detail' : 'poll_vote')
        history.replace(hasVoted ? `/polls/${pollId}` : `/polls/${pollId}/vote`)
      })
      .catch((e) => err(e))
    return () => {
      canceled = true
    }
  }, [pollIdParam])

  useEffect(() => {
    if (viewMode !== 'list_edit' && editListDraft) {
      setEditListDraft(undefined)
    }
  }, [viewMode, editListDraft])

  useEffect(() => {
    if (gameState === 'pre') {
      setHasAnyGuess(false)
      return
    }
    if (mode === 'single') return
    const guessed =
      listItems.length > 0 &&
      listItems.some((item) => item.team || item.guessed || item.pendingTeam)
    setHasAnyGuess(guessed)
  }, [gameState, mode, listItems])

  useEffect(() => {
    if (listId || pollIdParam) return
    const routePollId = params.pollId ? Number(params.pollId) : undefined
    if (routePollId && !Number.isNaN(routePollId)) {
      if (!experienceRef.current) setExperience('solo')
      setMode((current) => current || 'single')
      setShowModeSelectOnly(false)
      if (pollBackOriginRef.current !== 'random') {
        setPollBackToRandom(false)
        pollBackOriginRef.current = 'polls'
      }
      pollState.setPollId(routePollId)
      setSelectedListTab('polls')
      setViewMode(
        params.pollAction === 'play'
          ? 'poll_play'
          : params.pollAction === 'vote'
            ? 'poll_vote'
            : 'poll_detail'
      )
      finalizeBoot()
      return
    }
    const storedCode = sanitizeLobbyCode(
      localStorage.getItem(ACTIVE_LOBBY_CODE_KEY) || ''
    )
    const storedType = localStorage.getItem(ACTIVE_LOBBY_MATCH_TYPE_KEY)
    const started = localStorage.getItem(ACTIVE_LOBBY_GAME_STARTED_KEY) === '1'
    const entry = resolvePlayEntry(
      params.mode,
      joinCodeParam,
      MATCHMAKING_ENABLED || (storedType === 'matchmaking' && started && !!storedCode)
    )
    if (entry.redirect && entry.redirect !== location.pathname) {
      if (entry.redirect === '/') clearStaleLobbyState()
      if (entry.message)
        setListAlert({ open: true, severity: 'info', message: entry.message })
      history.replace(
        entry.redirect +
          (joinCodeParam ? '?join=' + encodeURIComponent(joinCodeParam) : '')
      )
      return
    }
    if (entry.experience) {
      if (experienceRef.current !== entry.experience) {
        // Explicit modes override unrelated persisted sessions.
        if (
          storedCode &&
          ((storedType === 'matchmaking') !== (entry.experience === 'online') ||
            !['lobby', 'online'].includes(entry.experience))
        )
          clearStaleLobbyState()
        setExperience(entry.experience)
        const config = playExperiences[entry.experience]
        setMode(config.mode)
        setMultiMode(config.multiMode)
        setSelectedListTab(config.tab)
        setGameState('pre')
      }
      if (joinCodeParam) setJoinGameCode(joinCodeParam)
      if (!['lobby', 'online'].includes(entry.experience)) {
        setGameState('pre')
        setActivePollId(undefined)
        pollState.resetPollSelection()
      }
      setShowModeSelectOnly(false)
      setViewMode('home')
      finalizeBoot()
      return
    }
    if (storedCode && (storedType !== 'matchmaking' || started)) {
      setJoinGameCode(storedCode)
      history.replace(storedType === 'matchmaking' ? '/play/online' : '/play/lobby')
      return
    }
    clearStaleLobbyState()
    setExperience(undefined)
    setMode(undefined)
    setShowModeSelectOnly(true)
    setViewMode('home')
    finalizeBoot()
  }, [params.pollId, params.pollAction, params.mode, joinCodeParam, sanitizeLobbyCode])

  useEffect(() => {
    if (!joinCodeParam) return
    const clearJoinFromUrl = () => {
      const next = new URLSearchParams(location.search)
      if (!next.has('join')) return
      next.delete('join')
      const nextSearch = next.toString()
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      })
    }
    if (attemptedAutoJoinRef.current === joinCodeParam) {
      clearJoinFromUrl()
      return
    }
    if (mode !== 'multi' || multiMode !== 'online') return
    if (!ws || gameCode) return

    attemptedAutoJoinRef.current = joinCodeParam
    exitedLobbyCodesRef.current.delete(joinCodeParam)
    setJoinGameCode(joinCodeParam)
    const joinEvent = onlinePlayerSessionId ? 'resumeLobby' : 'joinLobby'
    reconnectJoinSocketRef.current = ws
    ws.send(
      JSON.stringify({
        event: joinEvent,
        data: {
          code: joinCodeParam,
          playerSessionId: onlinePlayerSessionId || undefined,
          anonId: onlineAnonId || undefined,
          icon: onlineProfileIcon,
        },
      })
    )
    clearJoinFromUrl()
  }, [
    joinCodeParam,
    mode,
    multiMode,
    ws,
    gameCode,
    onlineProfileName,
    onlineProfileIcon,
    onlinePlayerSessionId,
    onlineAnonId,
    history,
    location.pathname,
    location.search,
  ])

  useEffect(() => {
    if (joinCodeParam) return
    if (mode !== 'multi' || multiMode !== 'online') return
    if (!ws || gameCode) return
    const storedCode = sanitizeLobbyCode(
      localStorage.getItem(ACTIVE_LOBBY_CODE_KEY) || ''
    )
    if (!storedCode) return
    const storedMatchType = localStorage.getItem(ACTIVE_LOBBY_MATCH_TYPE_KEY) || ''
    const storedGameStarted = localStorage.getItem(ACTIVE_LOBBY_GAME_STARTED_KEY) === '1'
    if ((storedMatchType === 'matchmaking') !== (experienceRef.current === 'online'))
      return
    if (storedMatchType === 'matchmaking' && !storedGameStarted) {
      clearStaleLobbyState()
      return
    }
    if (attemptedAutoJoinRef.current === storedCode) return
    attemptedAutoJoinRef.current = storedCode
    setJoinGameCode(storedCode)
    const joinEvent = onlinePlayerSessionId ? 'resumeLobby' : 'joinLobby'
    reconnectJoinSocketRef.current = ws
    ws.send(
      JSON.stringify({
        event: joinEvent,
        data: {
          code: storedCode,
          playerSessionId: onlinePlayerSessionId || undefined,
          anonId: onlineAnonId || undefined,
          icon: onlineProfileIcon,
        },
      })
    )
  }, [
    joinCodeParam,
    mode,
    multiMode,
    ws,
    gameCode,
    onlineProfileName,
    onlineProfileIcon,
    onlinePlayerSessionId,
    onlineAnonId,
    sanitizeLobbyCode,
  ])

  useEffect(() => {
    if (mode !== 'multi' || multiMode !== 'online') return
    if (!ws || !gameCode) return
    if (onlineMatchType === 'matchmaking' && gameState === 'pre') return
    if (reconnectJoinSocketRef.current === ws) return
    reconnectJoinSocketRef.current = ws
    const joinEvent = onlinePlayerSessionId ? 'resumeLobby' : 'joinLobby'
    ws.send(
      JSON.stringify({
        event: joinEvent,
        data: {
          code: gameCode,
          playerSessionId: onlinePlayerSessionId || undefined,
          anonId: onlineAnonId || undefined,
          icon: onlineProfileIcon,
        },
      })
    )
  }, [
    mode,
    multiMode,
    ws,
    gameCode,
    onlineMatchType,
    gameState,
    onlineProfileName,
    onlineProfileIcon,
    onlinePlayerSessionId,
    onlineAnonId,
  ])

  useEffect(() => {
    if (!MATCHMAKING_ENABLED || experience !== 'online') return

    getMatchmakingLeaderboard({ limit: 10, offset: 0 })
      .then((res) => {
        if (res.success && res.data) {
          setMatchmakingLeaderboard(res.data)
        }
      })
      .catch((e) => err(e))

    if (!appState?.user?.id) {
      setMyMatchmakingStats(undefined)
      return
    }

    getMyMatchmakingStats()
      .then((res) => {
        if (res.success) {
          setMyMatchmakingStats(res.data)
        }
      })
      .catch((e) => err(e))
  }, [experience, mode, multiMode, appState?.user?.id])

  useEffect(() => {
    if (gameState !== 'pre' || selectedTab !== 'play') return
    if (!appState?.user?.id) return

    getSoloStats()
      .then((res) => {
        if (res.success && res.data) {
          setSoloStats(res.data)
        }
      })
      .catch((e) => err(e))

    if (mode === 'multi' && multiMode === 'online') {
      getMyMatchmakingStats()
        .then((res) => {
          if (res.success) {
            setMyMatchmakingStats(res.data)
          }
        })
        .catch((e) => err(e))
    }
  }, [appState?.user?.id, gameState, mode, multiMode, selectedTab])

  const handleSelectTab = (tab: string) => {
    contentEpochRef.current += 1
    setLoading(false)
    setPollLoading(false)
    // clear list
    if (tab === 'generate') {
      setSelectedList(0)
      setListItems([])
      setTopic('')
    }

    if (location.search) {
      history.replace({ pathname: location.pathname, search: '' })
    }
    if (location.pathname !== homePath) {
      history.push(homePath)
      setViewMode('home')
    }
    if (tab === 'polls') {
      pollState.resetPollSelection()
    }
    if (tab !== 'random') {
      setPollBackToRandom(false)
      pollBackOriginRef.current = 'polls'
    }
    setSelectedListTab(tab)
  }

  useEffect(
    () =>
      history.block((next) => {
        const current = experienceRef.current
        if (!current) return
        const target = /^\/play\/([^/]+)$/.exec(next.pathname)?.[1]
        const leaving =
          next.pathname === '/' ||
          (target && (target === 'multi' ? 'local' : target) !== current) ||
          !/^\/(play|lists|polls)(\/|$)/.test(next.pathname)
        if (leaving && !exitExperienceRef.current()) return false
        return undefined
      }),
    [history]
  )

  const handleLogout = async () => {
    if (experienceRef.current && !exitExperienceRef.current()) return
    const result = await logout()
    if (result.success) {
      appReducer({ type: 'jwt', payload: undefined })
      appReducer({ type: 'user', payload: undefined })
      Cookies.remove('uuid', { path: '' })
      clearAuthStorage()
      history.push('/login')
    }
  }

  const handleDeAuth = () => {
    appReducer({ type: 'jwt', payload: undefined })
    appReducer({ type: 'user', payload: undefined })
    Cookies.remove('uuid', { path: '' })
    clearAuthStorage()
    setWs(undefined)
    setWsAuthenticated(false)
    setPendingMatch(false)
  }

  useEffect(() => {
    let closed = false
    let reconnectTimeout: number | undefined
    let activeSocket: WebSocket | undefined

    const connectSocket = () => {
      const knownConnectionId = connectionId || Cookies.get('uuid')
      const wsUrl = knownConnectionId
        ? `${socketUrl}?uuid=${knownConnectionId}`
        : socketUrl
      const websocket = new WebSocket(wsUrl)
      activeSocket = websocket

      websocket.addEventListener('open', () => {
        if (closed) return
        setWs(websocket)
        setWsAuthenticated(false)
        const jwt = getAuthToken()
        if (jwt) {
          websocket.send(
            JSON.stringify({
              event: 'auth',
              data: {
                token: jwt,
              },
            })
          )
        }
      })

      const resolveSelfPlayer = (playersList: Player[]): Player | undefined => {
        if (!playersList?.length) return undefined
        const byConnection = playersList.find((p) => p.id === connectionIdRef.current)
        if (byConnection) return byConnection
        const currentUserId = currentUserIdRef.current
        if (typeof currentUserId === 'number') {
          const byUser = playersList.find((p) => p.userId === currentUserId)
          if (byUser) return byUser
        }
        if (!currentUserId) {
          const byAnon = playersList.find(
            (p) => !!p.anonId && p.anonId === onlineAnonIdRef.current
          )
          if (byAnon) return byAnon
        }
        return undefined
      }

      websocket.addEventListener('close', () => {
        setWs(undefined)
        setWsAuthenticated(false)
        if (closed) return
        reconnectTimeout = window.setTimeout(connectSocket, 3000)
      })

      websocket.addEventListener('message', (event) => {
        const message = JSON.parse(event.data)
        const gameEvents = [
          'createdLobby',
          'madeMatch',
          'joinedLobby',
          'resumedLobby',
          'closedLobby',
          'endedGame',
          'matchEnded',
          'startedGame',
          'editedGame',
          'selectedOption',
          'missedOption',
          'updatedPlayers',
          'changedTeam',
          'voteState',
          'rematchState',
          'playerJoinedLobby',
          'leftLobby',
          'changedName',
          'changedIcon',
          'teamChanged',
          'updatedVote',
          'updatedRematch',
        ]
        const code = message.data?.code || message.data?.gameCode
        if (
          gameEvents.includes(message.event) &&
          (!['lobby', 'online'].includes(experienceRef.current || '') ||
            (code && exitedLobbyCodesRef.current.has(code)))
        ) {
          if (
            message.event === 'createdLobby' &&
            code &&
            !exitedLobbyCodesRef.current.has(code)
          ) {
            exitedLobbyCodesRef.current.add(code)
            websocket.send(JSON.stringify({ event: 'closeLobby', data: { code } }))
          }
          return
        }
        if (message.event === 'madeMatch' && !MATCHMAKING_ENABLED) return
        switch (message.event) {
          case 'connected': {
            if (message.data) {
              setConnectionId(message.data)
              Cookies.set('uuid', message.data, { path: '' })
            }
            return
          }
          case 'auth_success': {
            setWsAuthenticated(true)
            return
          }
          case 'auth_fail': {
            setWsAuthenticated(false)
            setListAlert({
              open: true,
              severity: 'error',
              message: 'Your session expired. Please log in again.',
            })
            handleDeAuth()
            websocket.close()
            return
          }
          case 'error': {
            if (message.message === 'Authentication required for online multiplayer.') {
              setWsAuthenticated(false)
              const jwt = getAuthToken()
              if (jwt) {
                websocket.send(
                  JSON.stringify({
                    event: 'auth',
                    data: {
                      token: jwt,
                    },
                  })
                )
              }
            }
            const errorMessage = String(message.message || '')
            if (
              errorMessage === 'Lobby not found.' ||
              errorMessage === 'Lobby no longer exists.' ||
              errorMessage === 'Lobby closed.'
            ) {
              if (!MATCHMAKING_ENABLED && experienceRef.current === 'online') {
                resetMatchmakingSessionState()
                setListAlert({
                  open: true,
                  severity: 'info',
                  message: MATCHMAKING_UNAVAILABLE,
                })
                return
              }
              const storedMatchType =
                localStorage.getItem(ACTIVE_LOBBY_MATCH_TYPE_KEY) || ''
              const storedGameStarted =
                localStorage.getItem(ACTIVE_LOBBY_GAME_STARTED_KEY) === '1'
              if (storedMatchType === 'matchmaking' && !storedGameStarted) {
                clearStaleLobbyState()
                return
              }
              if (
                pendingMatchRef.current ||
                onlineMatchTypeRef.current === 'matchmaking'
              ) {
                clearStaleLobbyState()
                return
              }
              clearStaleLobbyState()
              if (experienceRef.current === 'lobby') {
                setGameState('pre')
                setViewMode('home')
                setSelectedListTab('online')
              }
              setListAlert({
                open: true,
                severity: 'info',
                message:
                  'That lobby is no longer available. You can join or create a new lobby.',
              })
              return
            }
            setListAlert({
              open: true,
              severity: 'error',
              message: errorMessage || 'Online action failed.',
            })
            return
          }
          case 'onlineCount': {
            const nextAll = Number(
              message.data?.onlineUsersAll ?? message.data?.onlineUsers ?? 0
            )
            const nextLogged = Number(message.data?.onlineUsersLogged ?? 0)
            setOnlineUsersAll(nextAll)
            setOnlineUsersLogged(nextLogged)
            setSearchingRanked(Number(message.data?.searchingRanked ?? 0))
            setSearchingUnranked(Number(message.data?.searchingUnranked ?? 0))
            return
          }
          case 'close': {
            return
          }
          case '__ping__': {
            websocket.send(JSON.stringify({ event: '__pong__' }))
            return
          }
          case 'createdLobby': {
            handlePlayBoopRef.current()
            setPendingMatch(false)
            setOnlineMatchType('lobby')
            setOnlineMatchRated(false)
            localStorage.setItem(ACTIVE_LOBBY_MATCH_TYPE_KEY, 'lobby')
            localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
            setHostId(message.data.hostId)
            setGameCode(message.data.code)
            localStorage.setItem(ACTIVE_LOBBY_CODE_KEY, String(message.data.code || ''))
            const nextPlayers = message.data.players.slice()
            setPlayers(nextPlayers)
            const selfPlayer = resolveSelfPlayer(nextPlayers)
            if (selfPlayer?.playerSessionId) {
              setOnlinePlayerSessionId(selfPlayer.playerSessionId)
              localStorage.setItem(
                ACTIVE_LOBBY_PLAYER_SESSION_KEY,
                selfPlayer.playerSessionId
              )
            }
            if (selfPlayer?.anonId) {
              setOnlineAnonId(selfPlayer.anonId)
              localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, selfPlayer.anonId)
            }
            return
          }
          case 'madeMatch': {
            const newPlayers = message.data.players.slice()
            const hasOpponent = newPlayers.length >= 2
            setOnlineMatchType('matchmaking')
            setOnlineMatchRated(message.data?.rated !== false)
            if (hasOpponent) {
              setHostId(message.data.hostId)
              setGameCode(message.data.code)
              localStorage.setItem(ACTIVE_LOBBY_CODE_KEY, String(message.data.code || ''))
              localStorage.setItem(ACTIVE_LOBBY_MATCH_TYPE_KEY, 'matchmaking')
              localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
            } else {
              // Pending search is not a resumable lobby session.
              setHostId('')
              setGameCode('')
              localStorage.removeItem(ACTIVE_LOBBY_CODE_KEY)
              localStorage.removeItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY)
              localStorage.removeItem(ACTIVE_LOBBY_MATCH_TYPE_KEY)
              localStorage.removeItem(ACTIVE_LOBBY_GAME_STARTED_KEY)
            }
            setPlayers(newPlayers)
            const selfPlayer = resolveSelfPlayer(newPlayers)
            if (hasOpponent && selfPlayer?.playerSessionId) {
              setOnlinePlayerSessionId(selfPlayer.playerSessionId)
              localStorage.setItem(
                ACTIVE_LOBBY_PLAYER_SESSION_KEY,
                selfPlayer.playerSessionId
              )
            }
            if (selfPlayer?.anonId) {
              setOnlineAnonId(selfPlayer.anonId)
              localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, selfPlayer.anonId)
            }
            if (message.data?.pollId) {
              setActivePollId(Number(message.data.pollId))
              setSelectedList(undefined)
            }
            setTopic(message.data.prompt || '')
            const endedListId =
              typeof message.data?.listId === 'number'
                ? Number(message.data.listId)
                : typeof message.data?.currentListId === 'number'
                  ? Number(message.data.currentListId)
                  : 0
            if (endedListId > 0) {
              setSelectedList(endedListId)
            }
            setListItems(normalizeAndShuffleOnlineItems(message.data.list))
            if (hasOpponent) {
              setPendingMatch(false)
            }
            return
          }
          case 'canceledMatchSearch': {
            setPendingMatch(false)
            return
          }
          case 'joinedLobby': {
            handlePlayBoopRef.current()
            setPendingMatch(false)
            setOnlineMatchType(
              message.data?.matchType === 'matchmaking' ? 'matchmaking' : 'lobby'
            )
            setOnlineMatchRated(message.data?.rated !== false)
            localStorage.setItem(
              ACTIVE_LOBBY_MATCH_TYPE_KEY,
              message.data?.matchType === 'matchmaking' ? 'matchmaking' : 'lobby'
            )
            localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
            setHostId(message.data.hostId || '')
            setJoinGameCode('')
            setGameCode(message.data.code)
            localStorage.setItem(ACTIVE_LOBBY_CODE_KEY, String(message.data.code || ''))
            setPlayers(message.data.players)
            const selfPlayer = resolveSelfPlayer(message.data.players || [])
            if (selfPlayer?.playerSessionId) {
              setOnlinePlayerSessionId(selfPlayer.playerSessionId)
              localStorage.setItem(
                ACTIVE_LOBBY_PLAYER_SESSION_KEY,
                selfPlayer.playerSessionId
              )
            }
            if (selfPlayer?.anonId) {
              setOnlineAnonId(selfPlayer.anonId)
              localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, selfPlayer.anonId)
            }
            setTopic(message.data.prompt || '')
            const startedListId =
              typeof message.data?.listId === 'number'
                ? Number(message.data.listId)
                : typeof message.data?.currentListId === 'number'
                  ? Number(message.data.currentListId)
                  : 0
            if (startedListId > 0) {
              setSelectedList(startedListId)
            }
            setListItems(normalizeAndShuffleOnlineItems(message.data.list))
            return
          }
          case 'resumedLobby': {
            if (
              !MATCHMAKING_ENABLED &&
              message.data?.matchType === 'matchmaking' &&
              !message.data?.game?.started
            ) {
              websocket.send(
                JSON.stringify({
                  event: 'leaveLobby',
                  data: { code: message.data.code },
                })
              )
              resetMatchmakingSessionState()
              return
            }
            handlePlayBoopRef.current()
            setPendingMatch(false)
            setOnlineMatchType(
              message.data?.matchType === 'matchmaking' ? 'matchmaking' : 'lobby'
            )
            setOnlineMatchRated(message.data?.rated !== false)
            localStorage.setItem(
              ACTIVE_LOBBY_MATCH_TYPE_KEY,
              message.data?.matchType === 'matchmaking' ? 'matchmaking' : 'lobby'
            )
            setHostId(message.data.hostId || '')
            setJoinGameCode('')
            setGameCode(message.data.code)
            localStorage.setItem(ACTIVE_LOBBY_CODE_KEY, String(message.data.code || ''))
            setPlayers(message.data.players || [])
            const selfPlayer = resolveSelfPlayer(message.data.players || [])
            if (selfPlayer?.playerSessionId) {
              setOnlinePlayerSessionId(selfPlayer.playerSessionId)
              localStorage.setItem(
                ACTIVE_LOBBY_PLAYER_SESSION_KEY,
                selfPlayer.playerSessionId
              )
            }
            if (selfPlayer?.anonId) {
              setOnlineAnonId(selfPlayer.anonId)
              localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, selfPlayer.anonId)
            }
            setTopic(message.data.prompt || '')
            const resumedListId =
              typeof message.data?.listId === 'number'
                ? Number(message.data.listId)
                : typeof message.data?.currentListId === 'number'
                  ? Number(message.data.currentListId)
                  : 0
            if (resumedListId > 0) {
              setSelectedList(resumedListId)
            }
            setListItems(normalizeAndShuffleOnlineItems(message.data.list))
            if (message.data?.game?.started) {
              localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '1')
              setSelectedTab('play')
              setSelectedListTab('online')
              setViewMode('multi')
              setGameState('active')
              setAuthoritativeOnlineAction({
                nonce: Date.now(),
                // sentinel no-op index; we only want to sync turn/scores on resume.
                selectedIndex: -1,
                result: 'hit',
                teamTurn:
                  typeof message.data.game.teamTurn === 'number'
                    ? message.data.game.teamTurn
                    : 1,
                teamOneScore:
                  typeof message.data.game.teamOneScore === 'number'
                    ? message.data.game.teamOneScore
                    : 0,
                teamTwoScore:
                  typeof message.data.game.teamTwoScore === 'number'
                    ? message.data.game.teamTwoScore
                    : 0,
              })
              const pendingVote = message.data.game.pendingVote
              if (pendingVote?.deadlineAtMs && pendingVote?.votesByConnection) {
                setOnlineVoteState({
                  code: String(message.data.code || ''),
                  team: Number(pendingVote.team || 0),
                  deadlineAtMs: Number(pendingVote.deadlineAtMs || 0),
                  votes: Object.entries(pendingVote.votesByConnection)
                    .filter(([, value]) => typeof value === 'number')
                    .map(([connectionId, selectedIndex]) => ({
                      connectionId,
                      selectedIndex: Number(selectedIndex),
                    })),
                })
              } else {
                setOnlineVoteState(undefined)
              }
            } else {
              localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
              setOnlineVoteState(undefined)
              setOnlineRematchState(undefined)
            }
            return
          }
          case 'playerJoinedLobby': {
            handlePlayBoopRef.current()
            setPlayers((nextPlayers) => nextPlayers.concat([message.data.player]))
            return
          }
          case 'leftLobby': {
            setGameCode(message.data.code)
            setPlayers(message.data.players)
            return
          }
          case 'closedLobby': {
            if (code) exitedLobbyCodesRef.current.add(code)
            if (experienceRef.current === 'online') {
              resetMatchmakingSessionState()
              return
            }
            contentEpochRef.current += 1
            localStorage.removeItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY)
            setOnlinePlayerSessionId('')
            localStorage.removeItem(ACTIVE_LOBBY_CODE_KEY)
            localStorage.removeItem(ACTIVE_LOBBY_MATCH_TYPE_KEY)
            localStorage.removeItem(ACTIVE_LOBBY_GAME_STARTED_KEY)
            setGameCode('')
            setJoinGameCode('')
            setHostId('')
            setPendingMatch(false)
            setOnlineMatchType(undefined)
            setOnlineMatchRated(undefined)
            setOnlineMatchTrophyDelta(undefined)
            setMultiplayerXpAwarded(undefined)
            setOnlineVoteState(undefined)
            setOnlineRematchState(undefined)
            setAuthoritativeOnlineAction(undefined)
            setPlayers([])
            setGameState('pre')
            setViewMode('home')
            setSelectedListTab('online')
            setListItems([])
            setTopic('')
            setSelectedList(undefined)
            setActivePollId(undefined)
            setLoading(false)
            setPollLoading(false)
            pollState.resetPollSelection()
            history.replace('/play/lobby')
            return
          }
          case 'endedGame': {
            localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
            setOnlineVoteState(undefined)
            setAuthoritativeOnlineAction({
              nonce: Date.now(),
              selectedIndex: -1,
              teamTurn:
                typeof message.data?.teamTurn === 'number'
                  ? Number(message.data.teamTurn)
                  : 1,
              teamOneScore:
                typeof message.data?.teamOneScore === 'number'
                  ? Number(message.data.teamOneScore)
                  : undefined,
              teamTwoScore:
                typeof message.data?.teamTwoScore === 'number'
                  ? Number(message.data.teamTwoScore)
                  : undefined,
            })
            const rated = message.data?.rated
            const matchType = message.data?.matchType
            const reason = String(message.data?.reason || '')
            const trophyDelta =
              typeof message.data?.trophyDelta === 'number'
                ? Number(message.data.trophyDelta)
                : undefined
            const xpDelta =
              typeof message.data?.xpDelta === 'number'
                ? Number(message.data.xpDelta)
                : undefined
            if (matchType === 'matchmaking' && rated && typeof trophyDelta === 'number') {
              setOnlineMatchTrophyDelta(trophyDelta)
            } else {
              setOnlineMatchTrophyDelta(undefined)
            }
            if (typeof xpDelta === 'number') {
              setMultiplayerXpAwarded(xpDelta)
            } else {
              setMultiplayerXpAwarded(undefined)
            }
            const endedListId =
              typeof message.data?.listId === 'number'
                ? Number(message.data.listId)
                : typeof message.data?.currentListId === 'number'
                  ? Number(message.data.currentListId)
                  : 0
            if (endedListId > 0) {
              setSelectedList(endedListId)
            }
            if (appState?.user?.id) {
              getSoloStats()
                .then((res) => {
                  if (res.success && res.data) {
                    setSoloStats(res.data)
                  }
                })
                .catch((e) => err(e))
              if (matchType === 'matchmaking') {
                getMyMatchmakingStats()
                  .then((res) => {
                    if (res.success) {
                      setMyMatchmakingStats(res.data)
                    }
                  })
                  .catch((e) => err(e))
              }
            }
            const isForfeitEnd =
              reason === 'disconnect_forfeit' || reason === 'leave_forfeit'
            if (matchType === 'matchmaking') {
              if (isForfeitEnd) {
                resetMatchmakingSessionState()
              }
              if (!isForfeitEnd) {
                setGameState('over')
              }
              if (
                typeof message.data?.rematchDeadlineAtMs === 'number' &&
                Array.isArray(message.data?.rematchVoters)
              ) {
                setOnlineRematchState({
                  code: String(gameCode || ''),
                  deadlineAtMs: Number(message.data.rematchDeadlineAtMs),
                  voters: message.data.rematchVoters.map((v: unknown) => String(v)),
                })
              } else {
                setOnlineRematchState(undefined)
              }
            } else {
              handleRestartGameRef.current()
              setOnlineRematchState(undefined)
            }
            if (reason === 'disconnect_forfeit') {
              setListAlert({
                open: true,
                severity: 'info',
                message: rated
                  ? 'Play forfeited due to disconnect. Returning to Online.'
                  : 'Match ended due to disconnect.',
              })
            } else if (reason === 'leave_forfeit') {
              setListAlert({
                open: true,
                severity: 'info',
                message: rated
                  ? 'Play forfeited. Match counts as a loss. Returning to Online.'
                  : 'A player left the match. Returning to Online.',
              })
            } else if (matchType === 'lobby') {
              // no-op: private lobby games do not need ranked/unranked messaging
            } else if (rated === false) {
              setListAlert({
                open: true,
                severity: 'info',
                message: '',
              })
            } else {
              setListAlert({
                open: true,
                severity: 'success',
                message: '',
              })
            }
            return
          }
          case 'changedTeam': {
            handlePlayClickRef.current()
            setPlayers(message.data.players)
            return
          }
          case 'changedName': {
            handlePlayClickRef.current()
            setPlayers(message.data.players)
            return
          }
          case 'changedIcon': {
            handlePlayClickRef.current()
            setPlayers(message.data.players)
            return
          }
          case 'rematchState': {
            if (!message.data?.code || !message.data?.deadlineAtMs) {
              setOnlineRematchState(undefined)
              return
            }
            setOnlineRematchState({
              code: String(message.data.code),
              deadlineAtMs: Number(message.data.deadlineAtMs),
              voters: Array.isArray(message.data.voters)
                ? message.data.voters.map((v: unknown) => String(v))
                : [],
            })
            return
          }
          case 'matchEnded': {
            localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '0')
            if (message.data?.reason === 'content_unavailable') {
              // Moderation cancels a private game, not the lobby or a ranked match.
              setGameState('pre')
              setViewMode('home')
              setSelectedTab('play')
              setSelectedListTab('online')
              setActivePollId(undefined)
              setSelectedList(undefined)
              setListItems([])
              setTopic('')
              setOnlineVoteState(undefined)
              setOnlineRematchState(undefined)
              setOnlineMatchTrophyDelta(undefined)
              setMultiplayerXpAwarded(undefined)
              setListAlert({
                open: true,
                severity: 'info',
                message:
                  message.message ||
                  'This question is no longer available. Choose another game.',
              })
              return
            }
            resetMatchmakingSessionState()
            setOnlineMatchTrophyDelta(undefined)
            setMultiplayerXpAwarded(undefined)
            const reason = String(message.data?.reason || '')
            const disconnectedConnectionId =
              typeof message.data?.disconnectedConnectionId === 'string'
                ? String(message.data.disconnectedConnectionId)
                : ''
            const currentConnectionId = connectionIdRef.current || connectionId || ''
            const selfLeft =
              !!disconnectedConnectionId &&
              disconnectedConnectionId === currentConnectionId
            if (reason === 'rematch_timeout') {
              setListAlert({
                open: true,
                severity: 'info',
                message: 'Match ended. Play Again timed out.',
              })
            } else if (reason === 'left_match' || reason === 'leave_forfeit') {
              setListAlert({
                open: true,
                severity: 'info',
                message: selfLeft
                  ? onlineMatchRated
                    ? 'Play forfeited. Match counts as a loss.'
                    : 'You left the match.'
                  : 'Opponent left the match. You win by forfeit.',
              })
            } else if (reason === 'post_game_leave') {
              setListAlert({
                open: true,
                severity: 'info',
                message: selfLeft ? 'Left match.' : 'Opponent left after game end.',
              })
            } else if (reason === 'disconnect_forfeit') {
              setListAlert({
                open: true,
                severity: 'info',
                message: 'Play forfeited due to disconnect.',
              })
            }
            return
          }
          case 'startedGame': {
            setActivePollId(
              message.data?.pollId ? Number(message.data.pollId) : undefined
            )
            playStart()
            localStorage.setItem(ACTIVE_LOBBY_GAME_STARTED_KEY, '1')
            setOnlineMatchTrophyDelta(undefined)
            setMultiplayerXpAwarded(undefined)
            setOnlineVoteState(undefined)
            setOnlineRematchState(undefined)
            const startedListId =
              typeof message.data?.listId === 'number'
                ? Number(message.data.listId)
                : typeof message.data?.currentListId === 'number'
                  ? Number(message.data.currentListId)
                  : 0
            if (startedListId > 0) {
              setSelectedList(startedListId)
            }
            if (message.data?.prompt) {
              setTopic(String(message.data.prompt))
            }
            if (Array.isArray(message.data?.list)) {
              setListItems(normalizeAndShuffleOnlineItems(message.data.list))
            }
            setAuthoritativeOnlineAction({
              nonce: Date.now(),
              selectedIndex: -1,
              teamTurn:
                typeof message.data?.teamTurn === 'number'
                  ? Number(message.data.teamTurn)
                  : 1,
              teamOneScore:
                typeof message.data?.teamOneScore === 'number'
                  ? Number(message.data.teamOneScore)
                  : 0,
              teamTwoScore:
                typeof message.data?.teamTwoScore === 'number'
                  ? Number(message.data.teamTwoScore)
                  : 0,
            })
            startGameRef.current()
            return
          }
          case 'editedGame': {
            setListItems(message.data.list || [])
            setTopic(message.data.prompt || '')
            return
          }
          case 'selectedOption':
          case 'missedOption': {
            setOnlineVoteState(undefined)
            setOnlineRematchState(undefined)
            setAuthoritativeOnlineAction({
              nonce: Date.now(),
              selectedIndex: Number(message.data.selectedIndex),
              result: message.data.result === 'miss' ? 'miss' : 'hit',
              actingTeam:
                typeof message.data.actingTeam === 'number'
                  ? message.data.actingTeam
                  : undefined,
              teamTurn:
                typeof message.data.teamTurn === 'number'
                  ? message.data.teamTurn
                  : undefined,
              teamOneScore:
                typeof message.data.teamOneScore === 'number'
                  ? message.data.teamOneScore
                  : undefined,
              teamTwoScore:
                typeof message.data.teamTwoScore === 'number'
                  ? message.data.teamTwoScore
                  : undefined,
            })
            return
          }
          case 'voteState': {
            if (!message.data) {
              setOnlineVoteState(undefined)
              return
            }
            setOnlineVoteState({
              code: String(message.data.code || ''),
              team: Number(message.data.team || 0),
              deadlineAtMs: Number(message.data.deadlineAtMs || 0),
              votes: Array.isArray(message.data.votes)
                ? message.data.votes.map(
                    (vote: { connectionId: string; selectedIndex: number }) => ({
                      connectionId: String(vote.connectionId),
                      selectedIndex: Number(vote.selectedIndex),
                    })
                  )
                : [],
            })
            return
          }
          default:
            return
        }
      })
    }

    connectSocket()

    return () => {
      closed = true
      if (reconnectTimeout) {
        window.clearTimeout(reconnectTimeout)
      }
      if (activeSocket && activeSocket.readyState <= 1) {
        activeSocket.close()
      }
    }
    // We intentionally only bootstrap once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // const handleDeAuth = async () => {
  //   appReducer({ type: 'jwt', payload: undefined })
  //   appReducer({ type: 'user', payload: undefined })
  //   Cookies.remove('uuid', { path: '' })
  //   Cookies.remove('jwt', { path: '' })
  //   Cookies.remove('jwt_token', { path: '' })
  // }

  // const socketConnect = useCallback(() => {
  //   let websocket = ws
  //   if (!ws) {
  //     const uuid = connectionId || Cookies.get('uuid')
  //     if (uuid) {
  //       websocket = new WebSocket(socketUrl + `?uuid=${uuid}`)
  //     } else {
  //       websocket = new WebSocket(socketUrl)
  //     }
  //   }
  //   if (!websocket) return

  //   websocket.addEventListener('open', () => {
  //     const jwt = Cookies.get('jwt_token')
  //     if (jwt) {
  //       websocket?.send(
  //         JSON.stringify({
  //           event: 'auth',
  //           data: {
  //             token: jwt,
  //             // uuid: uuid,
  //           },
  //         })
  //       )
  //     }
  //     setWsOpen(true)
  //   })

  //   websocket.addEventListener('close', () => {
  //     // leave any lobbies
  //     // if (gameCode) {
  //     //   ws?.send(
  //     //     JSON.stringify({
  //     //       event: 'leaveLobby',
  //     //       data: {
  //     //         code: gameCode,
  //     //       },
  //     //     }),
  //     //   )
  //     // }

  //     setWsOpen(false)
  //     console.log('WebSocket is closed.')

  //     // re connect to web socket
  //     if (!ws || !wsOpen) {
  //       console.log('Reconnecting to web socket')
  //       setTimeout(socketConnect, 15000)
  //     }
  //   })

  //   window.onbeforeunload = () => {
  //     // leave any lobbies
  //     if (gameCode) {
  //       ws?.send(
  //         JSON.stringify({
  //           event: 'leaveLobby',
  //           data: {
  //             code: gameCode,
  //           },
  //         })
  //       )
  //     }

  //     console.log('WebSocket is closed.')
  //     return null
  //   }
  //   window.addEventListener('beforeunload', () => {
  //     // leave any lobbies
  //     if (gameCode) {
  //       ws?.send(
  //         JSON.stringify({
  //           event: 'leaveLobby',
  //           data: {
  //             code: gameCode,
  //           },
  //         })
  //       )
  //     }

  //     console.log('WebSocket is closed.')
  //     return null
  //   })

  //   websocket.addEventListener('message', (event) => {
  //     const message = JSON.parse(event.data)

  //     console.log(message)
  //     switch (message.event) {
  //       case 'connected': {
  //         console.log('Authenticated! Connection ID:', message.data)
  //         if (message.data) {
  //           setConnectionId(message.data)
  //           Cookies.set('uuid', message.data, { path: '' })
  //         }
  //         return
  //       }
  //       case 'auth_success': {
  //         return
  //       }
  //       case 'auth_fail': {
  //         console.error('Authentication failed')
  //         // websocket?.close()
  //         void handleDeAuth()
  //         return
  //       }
  //       case 'close': {
  //         console.log('Connection CLOSED')
  //         // re-op connection
  //         // setTimeout(socketConnect, 1000)
  //         return
  //       }
  //       case '__ping__': {
  //         console.log('Received ping', 'sending pong')
  //         websocket?.send(JSON.stringify({ event: '__pong__' }))
  //         return
  //       }

  //       case 'createdLobby': {
  //         handlePlayBoopRef.current()
  //         setHostId(message.data.hostId)
  //         setGameCode(message.data.code)
  //         const newPlayers = message.data.players.slice()
  //         setPlayers(newPlayers)
  //         return
  //       }

  //       case 'madeMatch': {
  //         setHostId(message.data.hostId)
  //         setGameCode(message.data.code)
  //         // setPendingMatch(false)
  //         const newPlayers = message.data.players.slice()
  //         setPlayers(newPlayers)
  //         setTopic(message.data.prompt || '')
  //         // setListItems(message.data.list || [])
  //         setListItems(
  //           // eslint-disable-next-line @typescript-eslint/no-explicit-any
  //           message.data.list?.map((item: any, i: number) => ({
  //             title: item.title,
  //             metric: item.metric,
  //             index: item.index || i + 1,
  //             points: 1,
  //           })) || []
  //         )

  //         if (newPlayers.length === 2) {
  //           setPendingMatch(false)
  //           startGameRef?.current?.()
  //         }

  //         return
  //       }

  //       case 'joinedLobby': {
  //         handlePlayBoopRef.current()
  //         setHostId('') // clear hostId since joining lobby
  //         setJoinGameCode('') // clear hostId since joining lobby
  //         setGameCode(message.data.code)
  //         setPlayers(message.data.players)
  //         setTopic(message.data.prompt || '')
  //         setListItems(message.data.list || [])
  //         return
  //       }
  //       case 'playerJoinedLobby': {
  //         handlePlayBoopRef.current()
  //         setPlayers((players) => players.concat([message.data.player]))
  //         return
  //       }

  //       case 'leftLobby': {
  //         setGameCode(message.data.code)
  //         setPlayers(message.data.players)
  //         return
  //       }

  //       case 'closedLobby': {
  //         handleCloseLobby()
  //         return
  //       }

  //       case 'endedGame': {
  //         handleRestartGame()
  //         return
  //       }

  //       case 'changedTeam': {
  //         handlePlayClickRef.current()
  //         setPlayers(message.data.players)
  //         return
  //       }

  //       case 'startedGame': {
  //         startGameRef.current()
  //         return
  //       }

  //       case 'editedGame': {
  //         handlePlayClickRef.current()
  //         const prompt = message.data.prompt
  //         const list = message.data.list
  //         setListItems(list)
  //         setTopic(prompt)
  //         return
  //       }
  //       case 'selectedOption': {
  //         // set guess from user
  //         handleItemClickRef
  //           .current(message.data.selectedIndex)
  //           .then(() => {
  //             handleGameOverRef.current()
  //           })
  //           .catch((e) => {
  //             err(e)
  //           })
  //       }
  //     }
  //   })

  //   setWs(websocket)
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [reconnectTimeout, setReconnectTimeout])

  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // useEffect(() => socketConnect(), [])

  // const [guessTime, setGuessTime] = useState<string>('')

  const {
    musicIsPlaying,
    playMusic,
    stopMusic,
    playPop1,
    playPop2,
    playCelebrate,
    playSelect,
    playWrong,
    playStart,
    playWin,
    playLoss,
    playBoop,
    playClick1,
    playClick2,
    playButton,
    playSwipe,
    playToggleOn,
    playToggleOff,
  } = useGameAudio({ audioEnabled, volumeLevel })

  const sounds = {
    click: playClick1,
    clickAlt: playClick2,
    swipe: playSwipe,
    celebrate: playCelebrate,
    wrong: playWrong,
    select: playSelect,
    start: playStart,
    win: playWin,
    loss: playLoss,
    boop: playBoop,
    button: playButton,
    toggleOn: playToggleOn,
    toggleOff: playToggleOff,
    pop1: playPop1,
    pop2: playPop2,
  }

  const pollState = usePollState({
    viewMode,
    gameState,
    sounds,
    setViewMode,
    setSelectedListTab,
    homePath,
    historyPush: history.push,
    startPollGame: (poll, options) => startPollGameRef.current(poll, options),
    onPollDeleted: (pollId) => {
      if (activePollId === pollId) {
        setActivePollId(undefined)
      }
    },
    err,
  })

  const viewKey = useMemo(() => {
    if (!booted) return 'boot'
    if (isPollPlayView) return 'pollSubmit'
    if (pollState.isPollDetailView) return 'pollDetail'
    if (gameState !== 'pre') return 'game'
    return showModeSelectOnly ? 'mode' : 'list'
  }, [booted, gameState, pollState.isPollDetailView, isPollPlayView, showModeSelectOnly])

  const [volumeExpanded, setVolumeExpanded] = useState<boolean>(false)

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  // Removed mobile scroll lock: rely on default scrolling behavior.

  useEffect(() => {
    const myPlayer = players.find((p) => p.id === connectionId)
    setMyPlayer(myPlayer)
    if (myPlayer?.name) {
      setOnlineProfileName(myPlayer.name)
    }
    if (myPlayer?.icon) {
      setOnlineProfileIcon(myPlayer.icon)
    }
    if (myPlayer?.playerSessionId) {
      setOnlinePlayerSessionId(myPlayer.playerSessionId)
      localStorage.setItem(ACTIVE_LOBBY_PLAYER_SESSION_KEY, myPlayer.playerSessionId)
    }
    if (myPlayer?.anonId) {
      setOnlineAnonId(myPlayer.anonId)
      localStorage.setItem(ACTIVE_LOBBY_ANON_ID_KEY, myPlayer.anonId)
    }
  }, [players, connectionId])

  useEffect(() => {
    if (dailyList) return
    getDailyList()
      .then((res) => {
        if (res.success && res.data) {
          setDailyList(res.data)
          try {
            const keepId = res.data.id
            const keys = Object.keys(localStorage)
            keys.forEach((key) => {
              if (key.startsWith('daily_progress_') || key.startsWith('daily_result_')) {
                if (!keepId || !key.endsWith(String(keepId))) {
                  localStorage.removeItem(key)
                }
              }
            })
          } catch {
            // ignore
          }
        }
      })
      .catch((e) => err(e))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!dailyList?.id) {
      setDailyResult(undefined)
      return
    }
    if (appState?.user?.id) {
      setDailyResult(undefined)
      return
    }
    const key = `daily_result_${dailyList.id}`
    const stored = localStorage.getItem(key)
    if (!stored) {
      setDailyResult(undefined)
      return
    }
    try {
      const parsed = JSON.parse(stored)
      const ts = Number(parsed?.ts || 0)
      const correct = Number(parsed?.correct || 0)
      const total = Number(parsed?.total || 0)
      const guesses = Number(parsed?.guesses || 0)
      const completionMs = Number(parsed?.completionMs || 0)
      const inferredResult =
        parsed?.result === 'win'
          ? 'win'
          : parsed?.result === 'loss'
            ? 'loss'
            : total > 0 && correct >= total
              ? 'win'
              : 'loss'
      if (!ts || Date.now() - ts > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key)
        setDailyResult(undefined)
        return
      }
      setDailyResult({
        correct: Math.max(0, correct),
        total: Math.max(0, total),
        guesses: Math.max(0, guesses),
        completionMs: Math.max(0, completionMs),
        result: inferredResult,
        rank: typeof parsed?.rank === 'number' ? parsed.rank : undefined,
        totalParticipants:
          typeof parsed?.totalParticipants === 'number'
            ? parsed.totalParticipants
            : undefined,
        percentile:
          typeof parsed?.percentile === 'number' ? parsed.percentile : undefined,
        topPercentLabel:
          typeof parsed?.topPercentLabel === 'string'
            ? parsed.topPercentLabel
            : undefined,
      })
    } catch {
      setDailyResult(undefined)
    }
  }, [dailyList?.id, appState?.user?.id])

  useEffect(() => {
    if (!dailyList?.id) {
      setDailyProgress(undefined)
      return
    }
    if (appState?.user?.id) {
      setDailyProgress(undefined)
      return
    }
    const resultKey = `daily_result_${dailyList.id}`
    const storedResult = localStorage.getItem(resultKey)
    if (storedResult) {
      try {
        const parsed = JSON.parse(storedResult)
        const ts = Number(parsed?.ts || 0)
        if (ts && Date.now() - ts <= 24 * 60 * 60 * 1000) {
          localStorage.removeItem(`daily_progress_${dailyList.id}`)
          setDailyProgress(undefined)
          return
        }
      } catch {
        // ignore
      }
    }
    const progressKey = `daily_progress_${dailyList.id}`
    const stored = localStorage.getItem(progressKey)
    if (!stored) {
      setDailyProgress(undefined)
      return
    }
    try {
      const parsed = JSON.parse(stored)
      const updatedAt = Number(parsed?.updatedAt || 0)
      if (!updatedAt || Date.now() - updatedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(progressKey)
        setDailyProgress(undefined)
        return
      }
      setDailyProgress(parsed)
    } catch {
      setDailyProgress(undefined)
    }
  }, [dailyList?.id, appState?.user?.id])

  const handleDailyProgressChange = (next?: DailyProgressState) => {
    if (!dailyList?.id) return
    if (appState?.user?.id) {
      setDailyProgress(next)
      return
    }
    const key = `daily_progress_${dailyList.id}`
    if (!next) {
      localStorage.removeItem(key)
      setDailyProgress(undefined)
      return
    }
    localStorage.setItem(key, JSON.stringify(next))
    setDailyProgress(next)
  }

  useEffect(() => {
    if (myGames.length) return
    if (selectedTab !== 'myGames') return

    getGames()
      .then((res) => {
        if (res.success && res.data?.length) {
          setMyGames(res.data)
        }
      })
      .catch((e) => err(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  // const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } =
  //   useWebSocket(socketUrl, {
  //     onOpen: () => console.log('opened'),
  //     // Will attempt to reconnect on all close events, such as server shutting down
  //     shouldReconnect: (closeEvent) => true,
  //   })

  // useEffect(() => {
  //   getWebSocket()?.addEventListener('update', (data) => {
  //     console.log(data)
  //   })
  //   // console.log('Connection state changed')
  //   // if (readyState === ReadyState.OPEN) {
  //   //   sendJsonMessage({
  //   //     event: 'subscribe',
  //   //     data: {
  //   //       channel: 'general-chatroom',
  //   //     },
  //   //   })
  //   // }
  // }, [readyState, sendJsonMessage])

  // useEffect(() => {
  //   console.log(`Socket Message: ${JSON.stringify(lastJsonMessage)}`)
  // }, [lastJsonMessage])

  const handleGameOverRef = useRef(() => {
    //
  })
  const sentMatchEndRef = useRef<string>('')
  const localMultiplayerAwardRef = useRef<string>('')

  const handleGameOver = () => {
    const allSelected = listItems.every(
      (item) => item.team !== undefined || item.pendingTeam
    )

    if (allSelected) {
      // setShowGuesses(true)
      setGameState('over')

      // if (teamOneScore > teamTwoScore) {
      //   play
      // }

      if (selectedList) {
        handlePlayedList(selectedList)
      }
    } else {
      return
    }
  }
  handleGameOverRef.current = handleGameOver

  useEffect(() => {
    if (gameState !== 'over') return
    if (multiMode !== 'online' || onlineMatchType !== 'matchmaking') return
    if (!gameCode || !ws) return
    if (sentMatchEndRef.current === gameCode) return
    sentMatchEndRef.current = gameCode
    handleEndGame()
  }, [gameState, multiMode, onlineMatchType, gameCode, ws])

  useEffect(() => {
    if (gameState === 'active' || gameState === 'pre') {
      sentMatchEndRef.current = ''
      setOnlineMatchTrophyDelta(undefined)
      setMultiplayerXpAwarded(undefined)
      localMultiplayerAwardRef.current = ''
    }
  }, [gameState, gameCode])

  useEffect(() => {
    if (gameState !== 'over') return
    if (typeof window === 'undefined') return
    playButton()
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }, [gameState])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const enteringListGame = gameState === 'active'
    const enteringPollFlow =
      viewMode === 'poll_vote' || viewMode === 'poll_play' || viewMode === 'poll_detail'
    if (!enteringListGame && !enteringPollFlow) return
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }, [gameState, viewMode, selectedList, activePollId])

  const handleEndGame = () => {
    const teamOneScore = listItems.filter((item) => item.team === 1).length
    const teamTwoScore = listItems.filter((item) => item.team === 2).length
    const endGameEvent = JSON.stringify({
      event: 'endGame',
      data: {
        code: gameCode,
        list: listItems,
        prompt: topic,
        players: players,
        teamOneScore: teamOneScore,
        teamTwoScore: teamTwoScore,
        teamOneId: myPlayer?.id,
        listId: selectedList,
        mode: mode,
        multiMode: multiMode,
      },
    })
    if (players.length > 1 && multiMode === 'party') {
      if (hostId === connectionId) {
        // if game active send ack
        ws?.send(endGameEvent)
      }
      return // no-op, cannot leave screen
    } else if (players.length > 1 && multiMode === 'online') {
      ws?.send(endGameEvent)
    }
  }

  const handleRestartGame = () => {
    if (activePollId && pollPlayBackTarget === 'detail') {
      const returnPollId = activePollId
      setActivePollId(undefined)
      setGameState('pre')
      setListItems([])
      setViewMode('poll_detail')
      setSelectedListTab('polls')
      history.replace(`/polls/${returnPollId}`)
      return
    }
    if (activePollId) history.replace(homePath)
    if (listId) {
      history.push(homePath)
    }
    const keepOnlineLobby =
      multiMode === 'online' && onlineMatchType === 'lobby' && !!gameCode
    if (multiMode === 'online' && !keepOnlineLobby) {
      // if game active send ack
      setGameCode('') // "leave lobby"
      setPlayers([])
      setTopic('')
      setListItems([])
      setPendingMatch(false)
      setOnlineMatchType(undefined)
      setOnlineMatchRated(undefined)
      setOnlineMatchTrophyDelta(undefined)
      setMultiplayerXpAwarded(undefined)
      setOnlineRematchState(undefined)
      playSelect()
      setMode('multi')
      setMultiMode('online')
    }
    if (keepOnlineLobby) {
      setGameState('pre')
      setSelectedTab('play')
      setSelectedListTab('online')
      setViewMode('home')
      setAuthoritativeOnlineAction(undefined)
      setOnlineVoteState(undefined)
      resetListTeams()
      return
    }

    setListRated(undefined)

    setActivePollId(undefined)
    setAuthoritativeOnlineAction(undefined)
    setOnlineVoteState(undefined)
    setOnlineRematchState(undefined)
    setSoloGameKey((prev) => prev + 1)
    pollState.resetPollSelection()
    setViewMode('home')
    if (pollBackToRandom && (activePollId || viewMode === 'poll_play')) {
      setSelectedListTab('random')
      setPollBackToRandom(false)
      pollBackOriginRef.current = null
    }

    // reset teams
    resetListTeams()
    setGameState('pre')
  }
  handleRestartGameRef.current = handleRestartGame

  useEffect(() => {
    if (gameState !== 'over') return
    if (mode !== 'multi' || multiMode !== 'local') return
    if (!appState?.user?.id) return

    const teamOneScore = listItems.filter((item) => item.team === 1).length
    const teamTwoScore = listItems.filter((item) => item.team === 2).length
    const question = listItems[0]?.question
    const signature = `${question?.sessionId || selectedList || 0}:${topic}:${teamOneScore}:${teamTwoScore}`
    if (localMultiplayerAwardRef.current === signature) return
    localMultiplayerAwardRef.current = signature
    const epoch = contentEpochRef.current

    const award = question
      ? finishQuestionLocal(
          question.pollId,
          question.sessionId,
          teamOneScore,
          teamOneScore > teamTwoScore
        ).then((res) => ({
          ...res,
          data: res.data
            ? { stats: res.data.stats, xpAwarded: res.data.result?.xpDelta }
            : undefined,
        }))
      : awardMultiplayerSoloXp(teamOneScore, teamOneScore > teamTwoScore)
    award
      .then((res) => {
        if (epoch !== contentEpochRef.current) return
        if (res.success && res.data) {
          setMultiplayerXpAwarded(res.data.xpAwarded)
          if (res.data.stats) setSoloStats(res.data.stats)
        }
      })
      .catch((e) => err(e))
  }, [appState?.user?.id, gameState, listItems, mode, multiMode, selectedList, topic])

  const handleNewPoll = (options?: { silentStart?: boolean }) => {
    const epoch = contentEpochRef.current
    const pathname = location.pathname
    const isCurrent = () =>
      epoch === contentEpochRef.current && pathname === window.location.pathname

    if (!requireLobby()) return
    pollLoadingSinceRef.current = Date.now()
    setPollLoading(true)
    const excludeId = activePollId ?? pollState.selectedPollId ?? undefined
    const resetPollPlayStateForVote = () => {
      setActivePollId(undefined)
      setGameState('pre')
      setListItems([])
      setTopic('')
      setSelectedList(undefined)
      setListRated(undefined)
      setAuthoritativeOnlineAction(undefined)
      setOnlineVoteState(undefined)
      setOnlineRematchState(undefined)
    }
    const finishPollLoading = () => {
      const startedAt = pollLoadingSinceRef.current || 0
      const elapsed = Date.now() - startedAt
      const minMs = 350
      const remaining = Math.max(0, minMs - elapsed)
      window.setTimeout(() => {
        if (epoch === contentEpochRef.current) setPollLoading(false)
      }, remaining)
    }
    const startPollFlow = (nextPoll: Poll, hasVoted?: boolean) => {
      if (!isCurrent()) return
      finishPollLoading()
      setDailyPollContext(undefined)
      setPollBackToRandom(false)
      pollBackOriginRef.current = 'polls'
      setPollPlayBackTarget('polls')
      setSelectedListTab('polls')
      setSelectedTab('play')
      if (mode !== 'single') {
        pollState.markPollPlayHandled(nextPoll.id!)
        pollState.handlePollPlay(nextPoll)
        startPollGame(nextPoll, { silentStart: options?.silentStart })
        return
      }
      resetPollPlayStateForVote()
      const shouldOpenDetail =
        (appState?.user?.id && nextPoll.creatorId === appState.user.id) ||
        Boolean(hasVoted)
      if (shouldOpenDetail) {
        pollState.handlePollSelect(nextPoll)
      } else {
        pollState.handlePollVote(nextPoll, false)
      }
    }
    const pickPoll = (nextPoll: Poll) => {
      if (!isCurrent()) return
      if (!nextPoll.id) return
      if (appState?.user?.id) {
        getPoll(nextPoll.id)
          .then((res) => {
            if (!isCurrent()) return
            if (res.success && res.data) {
              startPollFlow(res.data, Boolean(res.data.viewerSubmitted))
              return
            }
            startPollFlow(nextPoll, Boolean(nextPoll.viewerSubmitted))
          })
          .catch(() => startPollFlow(nextPoll, Boolean(nextPoll.viewerSubmitted)))
        return
      }
      const localKey = localStorage.getItem(`poll_guest_key_${nextPoll.id}`) || undefined
      if (localKey) {
        getPoll(nextPoll.id, { localKey })
          .then((res) => {
            if (!isCurrent()) return
            if (res.success && res.data) {
              startPollFlow(res.data, Boolean(res.data.viewerSubmitted))
              return
            }
            startPollFlow(nextPoll, Boolean(nextPoll.viewerSubmitted))
          })
          .catch(() => startPollFlow(nextPoll, Boolean(nextPoll.viewerSubmitted)))
        return
      }
      startPollFlow(nextPoll, Boolean(nextPoll.viewerSubmitted))
    }
    getRandomPoll({ excludeId })
      .then((res) => {
        if (!isCurrent()) return
        console.log('poll')
        if (!res.success || !res.data?.id) {
          finishPollLoading()
          setListAlert({
            open: true,
            severity: 'info',
            message: 'No other polls are available yet.',
          })
          return
        }
        const nextPoll = res.data
        if (!nextPoll.id) {
          finishPollLoading()
          setListAlert({
            open: true,
            severity: 'info',
            message: 'No other polls are available yet.',
          })
          return
        }
        pickPoll(nextPoll)
      })
      .catch(() => {
        finishPollLoading()
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Failed to load a new poll. Please try again.',
        })
      })
  }

  const handleNewGame = (options?: {
    silentPollStart?: boolean
    forceRandomOnlineLobby?: boolean
  }) => {
    if (experience === 'online' && !MATCHMAKING_ENABLED) {
      goToModeSelectClean()
      return
    }
    if (mode === 'single' && selectedListTab === 'daily') {
      setSelectedListTab('random')
      setSelectedList(undefined)
      setDailyResult(undefined)
      setDailyProgress(undefined)
    }
    if (activePollId || viewMode === 'poll_play') {
      handleNewPoll({ silentStart: options?.silentPollStart })
      return
    }
    if (multiMode === 'online' && onlineMatchType === 'lobby' && !!gameCode) {
      ws?.send(
        JSON.stringify({
          event: 'startGame',
          data: {
            code: gameCode,
            forceRandom: options?.forceRandomOnlineLobby !== false,
          },
        })
      )
      return
    }
    if (mode === 'multi' && multiMode === 'local') {
      setGameState('pre')
    }
    if (mode === 'host') {
      setGameState('pre')
      setSelectedList(undefined)
      setTopic('')
      setListItems([])
    }
    // Reset states
    if (multiMode === 'online') {
      // if game active send ack
      setGameCode('') // "leave lobby"
      setPlayers([])
      setTopic('')
      setListItems([])
      setPendingMatch(false)
      setOnlineMatchType(undefined)
      setOnlineMatchRated(undefined)
      playSelect()
      setMode('multi')
      setMultiMode('online')
    }

    setListRated(undefined)

    setActivePollId(undefined)
    setAuthoritativeOnlineAction(undefined)
    setOnlineVoteState(undefined)
    setOnlineRematchState(undefined)
    setSoloGameKey((prev) => prev + 1)

    // reset teams
    resetListTeams()

    setListItems([])
    setTopic('')
    const epoch = contentEpochRef.current
    wait(100)
      .then(() => {
        if (epoch !== contentEpochRef.current) return
        handleStartGame(mode === 'host' ? undefined : selectedList, false)
      })
      .catch((e) => err(e))
  }

  const handlePreScreen = () => {
    setGameState('pre')
    setViewMode('home')
    pollState.resetPollSelection()
    history.push(homePath)
    handleEndGame()
    handleRestartGame()
  }

  const resetListTeams = () => {
    setListItems((current) => current.map((item) => ({ ...item, team: undefined })))
  }

  const handleSelectExperience = (next: PlayExperience) => {
    playClick2()
    const config = playExperiences[next]
    clearStaleLobbyState()
    contentEpochRef.current += 1
    setExperience(next)
    setMode(config.mode)
    setMultiMode(config.multiMode)
    setSelectedListTab(config.tab)
    setSelectedTab('play')
    setGameState('pre')
    setListItems([])
    setTopic('')
    setSelectedList(undefined)
    setActivePollId(undefined)
    setViewMode('home')
    setShowModeSelectOnly(false)
    window.scrollTo({ top: 0, behavior: 'auto' })
    history.push(experiencePath(next))
  }

  const requireLobby = () => {
    if (experienceRef.current === 'online') {
      setListAlert({
        open: true,
        severity: 'info',
        message: MATCHMAKING_ENABLED
          ? 'Use Find a Match to start an online game.'
          : MATCHMAKING_UNAVAILABLE,
      })
      return false
    }
    if (experienceRef.current !== 'lobby' || gameCode) return true
    setListAlert({
      open: true,
      severity: 'info',
      message: 'Create or join a lobby first',
    })
    setGameState('pre')
    setViewMode('home')
    setSelectedListTab('online')
    pendingStartItemsRef.current = null
    setSelectedList(undefined)
    setTopic('')
    setListItems([])
    history.replace('/play/lobby')
    return false
  }

  const handleMusicToggle = () => {
    if (audioEnabled) {
      playToggleOff()
      stopMusic()
      setAudioEnabled(false)
      return
    }

    setAudioEnabled(true)
    playToggleOn()
    playMusic()
  }

  const handleStartToggle = () => {
    if (!audioEnabled) return
    if (!musicIsPlaying) {
      playMusic()
    }
  }

  const handleRateList = (rating: number) => {
    playClick1()
    if (!selectedList) return

    if (listRated === rating) {
      setListRated(undefined)
      return
    } else {
      setListRated(rating)
    }
    rateList(selectedList, rating)
      .then((res) => {
        console.log(res)
      })
      .catch((e) => err(e))
    // rate list
  }

  const handlePlayedList = (listId: number) => {
    if (!listId) return

    playedList(listId)
      .then((res) => {
        console.log(res)
      })
      .catch((e) => err(e))
    // rate list
  }

  const handleCreateList = () => {
    if (selectedList) {
      return
    }

    if (!topic || !listItems.length) {
      return
    }

    setSaving(true)
    const listItemsType = listItems.map((l) => ({
      listId: 0,
      title: l.title,
      points: 1,
      metric: l.metric,
      rank: l.index,
      index: l.index,
    }))

    createList({ prompt: topic, items: listItemsType })
      .then((res) => {
        setSaving(false)
        if (res.success && res.data) {
          setSelectedList(res.data.id)
        }
      })
      .catch((e) => {
        setSaving(false)
        err(e)
      })
  }

  const doCreate = async (): Promise<number | undefined> => {
    const listIdSet = selectedList
    if (listIdSet) {
      return listIdSet
    }

    if (!topic || !listItems.length) {
      return undefined
    }

    setSaving(true)
    const listItemsType = listItems.map((l) => ({
      listId: 0,
      title: l.title,
      points: 1,
      metric: l.metric,
      rank: l.index,
      index: l.index,
    }))

    return createList({ prompt: topic, items: listItemsType })
      .then((res) => {
        setSaving(false)
        if (res.success && res.data) {
          setSelectedList(res.data.id)
          return res.data.id
        }
      })
      .catch((e) => {
        setSaving(false)
        err(e)
        return undefined
      })
  }

  const handleHostPlayMode = () => {
    if (hostPlayMode === 'in-order') {
      playToggleOn()
      setHostPlayMode('any-order')
    } else {
      playToggleOff()
      setHostPlayMode('in-order')
    }
  }

  const handleCloseEmojiPreview = () => {
    setShowEmojiPreview(false)
    setSelectedPlayer(undefined)
  }

  // const changePlayerIcon = (playerId: string | undefined, icon: string) => {
  //   if (!playerId) return

  //   const playerIndex = players.findIndex((p) => p.id === playerId)
  //   if (playerIndex === -1) return

  //   const newPlayers = players.slice()
  //   const newPlayer = newPlayers[playerIndex]
  //   if (!newPlayer) return

  //   newPlayer.icon = icon
  //   newPlayers[playerIndex] = newPlayer

  //   setPlayers(newPlayers)
  // }

  const handleCreateLobby = (profile?: { name?: string; icon?: string }) => {
    if (!ws) {
      setListAlert({
        open: true,
        severity: 'warning',
        message: 'Connecting to online service. Try again in a second.',
      })
      return
    }
    const newList = listItems.map((item, i) => ({
      title: item.title,
      metric: item.metric,
      index: item.index || i + 1,
      points: 1,
    }))
    reconnectJoinSocketRef.current = ws
    ws.send(
      JSON.stringify({
        event: 'createLobby',
        data: {
          hostId: connectionId,
          anonId: onlineAnonId || undefined,
          icon: profile?.icon || onlineProfileIcon,
          list: newList,
          prompt: topic,
        },
      })
    )
  }

  const handleJoinLobby = (profile?: { name?: string; icon?: string }) => {
    const normalizedJoinCode = sanitizeLobbyCode(joinGameCode)
    if (!normalizedJoinCode) return
    if (!ws) {
      setListAlert({
        open: true,
        severity: 'warning',
        message: 'Connecting to online service. Try again in a second.',
      })
      return
    }
    const joinEvent = onlinePlayerSessionId ? 'resumeLobby' : 'joinLobby'
    exitedLobbyCodesRef.current.delete(normalizedJoinCode)
    reconnectJoinSocketRef.current = ws
    ws?.send(
      JSON.stringify({
        event: joinEvent,
        data: {
          code: normalizedJoinCode,
          playerSessionId: onlinePlayerSessionId || undefined,
          anonId: onlineAnonId || undefined,
          icon: profile?.icon || onlineProfileIcon,
        },
      })
    )
  }

  const handleChangeOnlineTeam = (team: number) => {
    if (!gameCode || !ws) return
    ws.send(
      JSON.stringify({
        event: 'changeTeam',
        data: {
          code: gameCode,
          team,
        },
      })
    )
  }

  const handleChangeOnlineIcon = (icon: string) => {
    setOnlineProfileIcon(icon)
    if (!gameCode || !ws) return
    ws.send(
      JSON.stringify({
        event: 'changeIcon',
        data: {
          code: gameCode,
          icon,
        },
      })
    )
  }

  const handleChangeOnlineName = (name: string) => {
    setOnlineProfileName(name)
  }

  const withUniqueGuestName = (rawName: string) => {
    const base = (rawName || '').trim() || 'Guest'
    const used = new Set(
      players
        .filter((player) => player.id !== connectionId)
        .map((player) => (player.name || '').trim().toLocaleLowerCase())
        .filter(Boolean)
    )
    if (!used.has(base.toLocaleLowerCase())) return base
    let suffix = 1
    while (used.has(`${base}${suffix}`.toLocaleLowerCase())) {
      suffix += 1
    }
    return `${base}${suffix}`
  }

  const handleCommitOnlineName = () => {
    if (appState?.user?.id) return
    const uniqueName = withUniqueGuestName(onlineProfileName)
    setOnlineProfileName(uniqueName)
    if (!gameCode || !ws) return
    ws.send(
      JSON.stringify({
        event: 'changeName',
        data: {
          code: gameCode,
          name: uniqueName,
        },
      })
    )
  }

  const handleStartOnlineLobbyGame = () => {
    if (!gameCode || !ws) return
    ws.send(
      JSON.stringify({
        event: 'startGame',
        data: {
          code: gameCode,
          forceRandom: true,
        },
      })
    )
  }

  const returnToLobbyEntry = () => {
    if (gameCode) exitedLobbyCodesRef.current.add(gameCode)
    contentEpochRef.current += 1
    randomListRequestRef.current += 1
    questionStartRef.current.sequence += 1
    pendingStartItemsRef.current = null
    clearStaleLobbyState()
    pollState.resetPollSelection()
    setTopic('')
    setListItems([])
    setSelectedList(undefined)
    setActivePollId(undefined)
    setGameState('pre')
    setViewMode('home')
    setSelectedListTab('online')
    setLoading(false)
    setPollLoading(false)
    history.replace('/play/lobby')
  }
  const handleLeaveLobby = () => {
    if (!gameCode || !window.confirm('Are you sure you want to leave the lobby?')) return
    ws?.send(JSON.stringify({ event: 'leaveLobby', data: { code: gameCode } }))
    returnToLobbyEntry()
  }
  const handleCloseLobby = () => {
    if (!gameCode || hostId !== connectionId) return
    if (
      players.length > 1 &&
      !window.confirm('Are you sure you want to close the lobby?')
    )
      return
    ws?.send(JSON.stringify({ event: 'closeLobby', data: { code: gameCode } }))
    returnToLobbyEntry()
  }
  handleCloseLobbyRef.current = handleCloseLobby

  const handlePlayAgainMatchmaking = () => {
    if (!MATCHMAKING_ENABLED) return
    if (!gameCode || !ws) return
    ws.send(
      JSON.stringify({
        event: 'playAgain',
        data: {
          code: gameCode,
        },
      })
    )
  }

  const handleLeaveMatchmakingSession = (skipConfirm?: boolean) => {
    if (!gameCode) return
    const postGameLeave = gameState === 'over'
    if (!skipConfirm && !postGameLeave) {
      const confirmed = window.confirm(
        onlineMatchRated
          ? 'Are you sure you want to leave? This will count as a ranked loss.'
          : 'Are you sure you want to leave?'
      )
      if (!confirmed) return
    }
    ws?.send(
      JSON.stringify({
        event: 'leaveLobby',
        data: {
          code: gameCode,
        },
      })
    )
    resetMatchmakingSessionState()
    setListAlert({
      open: true,
      severity: 'info',
      message: !MATCHMAKING_ENABLED
        ? 'Match finished. Choose your next way to play.'
        : postGameLeave
          ? 'Left match. Returning to Online.'
          : onlineMatchRated
            ? 'Play forfeited. Match counts as a loss. Returning to Online.'
            : 'Left match. Returning to Online.',
    })
  }

  const handleStartGame = (list?: number, sound?: boolean) => {
    if (!experienceRef.current || renderContentEpoch !== contentEpochRef.current) return
    if (!requireLobby()) return
    // playMusic()
    if (sound !== false) {
      playClick1()
    }
    handleStartToggle()
    if (
      mode === 'single' &&
      typeof list === 'number' &&
      list > 0 &&
      location.pathname !== `/lists/${list}/play`
    ) {
      history.push(`/lists/${list}/play`)
      return
    }
    if (sound !== false && mode !== 'single' && experience !== 'lobby') {
      playStart()
    }
    if (
      mode === 'multi' &&
      gameCode &&
      ((multiMode === 'party' && onlineMatchType !== 'lobby') ||
        (multiMode === 'online' && onlineMatchType === 'lobby'))
    ) {
      const pendingPreset = pendingStartItemsRef.current
      if (multiMode === 'online' && onlineMatchType === 'lobby' && pendingPreset) {
        pendingStartItemsRef.current = null
        const newList = pendingPreset.items.map((item, i) => ({
          title: item.title,
          metric: item.metric,
          index: item.index || i + 1,
          points: item.points || 1,
        }))
        setSelectedList(pendingPreset.listId)
        setTopic(pendingPreset.topic)
        setListItems(newList)
        ws?.send(
          JSON.stringify({
            event: 'editGame',
            data: {
              code: gameCode,
              prompt: pendingPreset.topic,
              list: newList,
              questionSessionId: pendingPreset.items[0]?.question?.sessionId,
            },
          })
        )
      }
      ws?.send(
        JSON.stringify({
          event: 'startGame',
          data: {
            code: gameCode,
            questionSessionId: pendingPreset?.items[0]?.question?.sessionId,
            forceRandom:
              multiMode === 'online' &&
              onlineMatchType === 'lobby' &&
              selectedListTab === 'online' &&
              !pendingPreset,
          },
        })
      )
      return
    }
    if (
      mode === 'single' &&
      selectedListTab === 'daily' &&
      dailyList?.id &&
      (list === undefined || list === dailyList.id) &&
      Array.isArray(dailyList.items) &&
      dailyList.items.length > 0
    ) {
      pendingStartItemsRef.current = {
        listId: dailyList.id,
        topic: dailyList.prompt || '',
        items: dailyList.items.map((item, i) => ({
          title: item.title,
          metric: item.metric,
          index: item.index || item.rank || i + 1,
          points: item.points || 1,
          listId: item.listId ?? dailyList.id,
        })),
      }
    }
    startGameRef.current(list)
  }

  const handleStartRandomList = () => {
    if (!requireLobby()) return
    const requestId = ++randomListRequestRef.current
    const isCurrent = () =>
      randomListRequestRef.current === requestId &&
      randomListContextRef.current === randomListContext
    setLoading(true)
    getRandomList(selectedList)
      .then((res) => {
        if (!isCurrent()) return
        if (res.success && res.data?.id) {
          if (mode === 'single') {
            history.push(`/lists/${res.data.id}/play`)
            return
          }
          // Only Solo uses the list-play route; other modes keep their game/lobby context.
          pendingStartItemsRef.current = {
            listId: res.data.id,
            topic: res.data.prompt || '',
            items: (res.data.items || []).map((item, i) => ({
              title: item.title,
              metric: item.metric,
              index: item.index || item.rank || i + 1,
              points: item.points || 1,
              listId: res.data?.id,
            })),
          }
          setActivePollId(undefined)
          setDailyPollContext(undefined)
          pollState.resetPollSelection()
          setListRated(undefined)
          setViewMode(mode === 'host' ? 'host' : 'multi')
          handleStartGame(res.data.id)
          return
        }
        setLoading(false)
        setListAlert({
          open: true,
          severity: 'info',
          message: 'No trivia lists are available yet.',
        })
      })
      .catch((e) => {
        if (!isCurrent()) return
        setLoading(false)
        err(e)
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Failed to load a random trivia list. Please try again.',
        })
      })
      .finally(() => {
        if (randomListRequestRef.current === requestId) setLoading(false)
      })
  }

  const handleStartRandomPoll = () => {
    const epoch = contentEpochRef.current
    const pathname = location.pathname
    const isCurrent = () =>
      epoch === contentEpochRef.current && pathname === window.location.pathname

    if (!requireLobby()) return
    const excludeId = activePollId ?? pollState.selectedPollId ?? undefined
    const resetPollPlayStateForVote = () => {
      setActivePollId(undefined)
      setGameState('pre')
      setListItems([])
      setTopic('')
      setSelectedList(undefined)
      setListRated(undefined)
      setAuthoritativeOnlineAction(undefined)
      setOnlineVoteState(undefined)
      setOnlineRematchState(undefined)
    }
    const startPollFlow = (poll: Poll, hasVoted?: boolean) => {
      if (!isCurrent()) return
      setDailyPollContext(undefined)
      setPollBackToRandom(false)
      pollBackOriginRef.current = 'polls'
      setPollPlayBackTarget('polls')
      setSelectedListTab('polls')
      setSelectedTab('play')
      if (mode !== 'single') {
        pollState.handlePollPlay(poll)
        return
      }
      resetPollPlayStateForVote()
      const shouldOpenDetail =
        (appState?.user?.id && poll.creatorId === appState.user.id) || Boolean(hasVoted)
      if (shouldOpenDetail) {
        pollState.handlePollSelect(poll)
      } else {
        pollState.handlePollVote(poll, false)
      }
    }
    const pickPoll = (poll: Poll) => {
      if (!isCurrent()) return
      if (!poll.id) return
      if (appState?.user?.id) {
        getPoll(poll.id)
          .then((res) => {
            if (!isCurrent()) return
            if (res.success && res.data) {
              startPollFlow(res.data, Boolean(res.data.viewerSubmitted))
              return
            }
            startPollFlow(poll, Boolean(poll.viewerSubmitted))
          })
          .catch(() => startPollFlow(poll, Boolean(poll.viewerSubmitted)))
        return
      }
      if (!appState?.user?.id) {
        const localKey = localStorage.getItem(`poll_guest_key_${poll.id}`) || undefined
        if (localKey) {
          getPoll(poll.id, { localKey })
            .then((res) => {
              if (!isCurrent()) return
              if (res.success && res.data) {
                startPollFlow(res.data, Boolean(res.data.viewerSubmitted))
                return
              }
              startPollFlow(poll, Boolean(poll.viewerSubmitted))
            })
            .catch(() => startPollFlow(poll, Boolean(poll.viewerSubmitted)))
          return
        }
      }
      startPollFlow(poll, Boolean(poll.viewerSubmitted))
    }

    getRandomPoll({ excludeId })
      .then((res) => {
        if (!isCurrent()) return
        if (res.success && res.data?.id) {
          console.log(res.data.id)
          pickPoll(res.data)
          return
        }
        return getRandomPoll()
          .then((fallback) => {
            if (fallback.success && fallback.data?.id) {
              pickPoll(fallback.data)
              return
            }
            setListAlert({
              open: true,
              severity: 'info',
              message: 'No polls are available yet.',
            })
          })
          .catch(() => {
            setListAlert({
              open: true,
              severity: 'info',
              message: 'Failed to load a poll. Please try again.',
            })
          })
      })
      .catch(() => {
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Failed to load a poll. Please try again.',
        })
      })
  }

  const handleStartDailyPoll = (type: 'today' | 'previous') => {
    setDailyPollContext(undefined)
    setLoading(true)
    getDailyPoll({ type })
      .then((res) => {
        if (!res.success || !res.data?.id) {
          setListAlert({
            open: true,
            severity: 'info',
            message: 'No daily poll is available right now.',
          })
          return
        }
        const poll = res.data
        setPollBackToRandom(false)
        pollBackOriginRef.current = 'polls'
        setPollPlayBackTarget('polls')
        setSelectedListTab('polls')
        setSelectedTab('play')
        setMode('single')
        setShowModeSelectOnly(false)
        setDailyPollContext(type)
        const hasVoted = Boolean(poll.viewerSubmitted)
        if (!poll.id) return
        pollState.setPollId(poll.id)
        if (hasVoted || (appState?.user?.id && poll.creatorId === appState.user.id)) {
          setViewMode('poll_detail')
          history.replace(`/polls/${poll.id}`)
        } else {
          setViewMode('poll_vote')
          history.replace(`/polls/${poll.id}/vote`)
        }
      })
      .catch(() => {
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Failed to load daily poll.',
        })
      })
      .finally(() => setLoading(false))
  }

  const handleEditList = (list: ListType) => {
    setEditListDraft(list)
    setSelectedTab('play')
    setSelectedListTab('find')
    setListSearchShowMine(true)
    setShowModeSelectOnly(false)
    setViewMode('list_edit')
  }

  const handleBackFromListEdit = () => {
    setEditListDraft(undefined)
    setViewMode('home')
    setSelectedTab('play')
    setSelectedListTab('find')
    setListSearchShowMine(true)
    setShowModeSelectOnly(false)
  }

  const handleStartCreatedList = (payload: {
    topic: string
    items: Item[]
    listId?: number
  }) => {
    if (!requireLobby()) return
    pendingStartItemsRef.current = {
      items: payload.items,
      topic: payload.topic,
      listId: payload.listId,
    }
    setSelectedTab('play')
    handleStartGame()
  }

  const questionStartRef = useRef<{ sequence: number; pendingId?: number }>({
    sequence: 0,
  })
  const startPollGame = async (poll: Poll, options?: { silentStart?: boolean }) => {
    if (!requireLobby()) return
    if (poll.pollFormat === 'community_answers' && !poll.canPlay) {
      setGameState('pre')
      setActivePollId(undefined)
      setViewMode('poll_detail')
      if (poll.id) history.replace(`/polls/${poll.id}`)
      setListAlert({
        open: true,
        severity: 'info',
        message: poll.answersClosedAt
          ? 'Needs 10 different answers to play.'
          : 'A playable community ranking is not ready yet.',
      })
      return
    }
    let items: Item[]
    if (poll.questionExperienceVersion === 2 && poll.id) {
      if (questionStartRef.current.pendingId === poll.id) return
      const sequence = ++questionStartRef.current.sequence
      questionStartRef.current.pendingId = poll.id
      const pathname = window.location.pathname
      const context = randomListContextRef.current
      setPollLoading(true)
      try {
        const game = await startQuestionGame(
          poll.id,
          !mode || mode === 'single'
            ? 'single'
            : mode === 'host'
              ? 'host'
              : multiMode === 'online'
                ? 'online'
                : 'local',
          globalThis.crypto.randomUUID(),
          gameCode || undefined
        )
        if (
          sequence !== questionStartRef.current.sequence ||
          pathname !== window.location.pathname ||
          context !== randomListContextRef.current
        )
          return
        if (!game.success || !game.data)
          throw new Error(game.message || 'Could not start this game.')
        items = game.data.items.map((item) => ({
          ...item,
          question: {
            sessionId: game.data!.id,
            pollId: poll.id!,
            groupId: item.id,
            rankingVersion: game.data!.rankingVersion,
          },
        }))
      } catch (error) {
        if (
          sequence !== questionStartRef.current.sequence ||
          pathname !== window.location.pathname ||
          context !== randomListContextRef.current
        )
          return
        setGameState('pre')
        setViewMode('poll_detail')
        history.replace('/polls/' + poll.id)
        setListAlert({
          open: true,
          severity: 'error',
          message: error instanceof Error ? error.message : 'Could not start this game.',
        })
        return
      } finally {
        if (sequence === questionStartRef.current.sequence) {
          questionStartRef.current.pendingId = undefined
          setPollLoading(false)
        }
      }
    } else {
      if (!poll.items?.length || !poll.crowdRank?.length) {
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Not enough poll submissions yet.',
        })
        return
      }
      items = poll.crowdRank
        .slice(0, 10)
        .map((id, idx) => {
          const item = poll.items?.find((entry) => String(entry.id ?? entry.index) === id)
          if (!item) return undefined
          return {
            title: item.title,
            metric: item.metric,
            rank: idx + 1,
            points: 0,
            index: idx + 1,
            listId: poll.id || 0,
          }
        })
        .filter(isDefined)
    }
    if (!items.length) {
      setListAlert({
        open: true,
        severity: 'info',
        message: 'Not enough poll submissions yet.',
      })
      return
    }
    // keep existing mode for polls
    setSelectedList(undefined)
    setActivePollId(poll.id)
    setSelectedListTab('polls')
    setViewMode('poll_play')
    if (poll.id) {
      if (poll.questionExperienceVersion !== 2) playedPoll(poll.id).catch((e) => err(e))
    }
    pendingStartItemsRef.current = {
      items,
      topic: poll.prompt,
    }
    handleStartGame(
      undefined,
      poll.questionExperienceVersion === 2 || options?.silentStart ? false : undefined
    )
  }
  startPollGameRef.current = startPollGame

  const handleMakeMatch = () => {
    if (!MATCHMAKING_ENABLED || experience !== 'online') return
    if (mode === 'multi' && multiMode === 'online') {
      if (pendingMatch) {
        ws?.send(
          JSON.stringify({
            event: 'cancelMatchSearch',
          })
        )
        setPendingMatch(false)
        return
      }
      const isRanked = matchmakingRated
      const requiresAuth = !!appState?.user?.id
      if (isRanked && !appState?.user?.id) {
        setListAlert({
          open: true,
          severity: 'info',
          message: 'Log in to queue for ranked matchmaking. Guests can play unranked.',
        })
        return
      }
      if (!ws || (requiresAuth && !wsAuthenticated)) {
        setListAlert({
          open: true,
          severity: 'warning',
          message: requiresAuth
            ? 'Connecting to online service. Authenticating session, try again in a second.'
            : 'Connecting to online service, try again in a second.',
        })
        return
      }
      ws?.send(
        JSON.stringify({
          event: 'makeMatch',
          data: {
            rated: isRanked,
          },
        })
      )

      setPendingMatch(true)
      return
    }
    // startGame()
  }

  useEffect(() => {
    if (appState?.user?.id) return
    setMatchmakingRated(false)
  }, [appState?.user?.id])

  const handlePlayBoopRef = useRef(() => {
    //
  })
  const handlePlayBoop = () => {
    playBoop()
  }
  handlePlayBoopRef.current = handlePlayBoop

  const handlePlayClickRef = useRef(() => {
    //
  })
  const handlePlayClick = () => {
    playClick1()
  }
  handlePlayClickRef.current = handlePlayClick

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startGameRef = useRef<(listId?: number) => void>((listId?: number) => {
    //
  })
  const startGame = (previousListId?: number) => {
    const epoch = contentEpochRef.current
    const isCurrent = () => epoch === contentEpochRef.current && !!experienceRef.current
    if (!isCurrent()) return
    if (pendingStartItemsRef.current) {
      const preset = pendingStartItemsRef.current
      pendingStartItemsRef.current = null
      selectedStartRetryRef.current = { attempts: 0, listId: undefined }

      setSelectedList(preset.listId)
      setTopic(preset.topic)

      const nextItems =
        mode === 'single'
          ? toSoloItems(preset.items, SINGLE_PLAY_ITEMS)
          : mode === 'host'
            ? sortIndex(preset.items)
            : sortAlpha(preset.items)

      setListItems(nextItems)
      setGameState('active')
      if (mode === 'single') {
        setSoloGameKey((prev) => prev + 1)
      }
      return
    }

    if (
      (mode === 'single' || multiMode === 'local' || mode === 'host') &&
      (!listItems.length || !topic)
    ) {
      if (
        typeof previousListId === 'number' &&
        previousListId > 0 &&
        (selectedList === previousListId || !selectedList)
      ) {
        const previousRetry = selectedStartRetryRef.current
        if (previousRetry.listId !== previousListId) {
          selectedStartRetryRef.current = {
            listId: previousListId,
            attempts: 1,
          }
          window.setTimeout(() => {
            if (isCurrent()) startGameRef.current(previousListId)
          }, 60)
          return
        }
        if (previousRetry.attempts < 8) {
          selectedStartRetryRef.current = {
            listId: previousListId,
            attempts: previousRetry.attempts + 1,
          }
          window.setTimeout(() => {
            if (isCurrent()) startGameRef.current(previousListId)
          }, 60)
          return
        }
        if (mode === 'single') {
          setLoading(true)
          getList(previousListId)
            .then((res) => {
              if (!isCurrent()) return
              if (res.success && res.data?.items?.length) {
                const mappedItems =
                  res.data.items?.map((item, i) => ({
                    title: item.title,
                    metric: item.metric,
                    index: item.index || item.rank || i + 1,
                    points: 1,
                    listId: res.data?.id || 0,
                  })) || []
                setSelectedList(res.data.id)
                setTopic(res.data.prompt || '')
                setListItems(toSoloItems(mappedItems, SINGLE_PLAY_ITEMS))
                setLoading(false)
                setGameState('active')
                setSoloGameKey((prev) => prev + 1)
                selectedStartRetryRef.current = {
                  attempts: 0,
                  listId: undefined,
                }
                return
              }
              setLoading(false)
            })
            .catch((e) => {
              setLoading(false)
              err(e)
            })
          return
        }
      }
      selectedStartRetryRef.current = { attempts: 0, listId: undefined }
      setLoading(true)
      // generate
      getRandomList(previousListId)
        .then((res) => {
          if (!isCurrent()) return
          if (res.success && res.data) {
            setSelectedList(res.data.id)
            setListItems(res.data.items || [])
            setTopic(res.data.prompt)
          }

          if (mode === 'host') {
            setListItems((list) => sortIndex(list))
          } else if (mode === 'single') {
            setListItems((list) => toSoloItems(list, SINGLE_PLAY_ITEMS))
          } else {
            setListItems((list) => sortAlpha(list))
          }

          setLoading(false)
          setGameState('active')
          selectedStartRetryRef.current = { attempts: 0, listId: undefined }

          if (mode === 'single') {
            setSoloGameKey((prev) => prev + 1)
          }

          return
        })
        .catch((e) => {
          setLoading(false)
          err(e)
        })
    }

    if (mode === 'host') {
      setListItems((list) => sortIndex(list))
    } else if (mode === 'single') {
      setListItems((list) => toSoloItems(list, SINGLE_PLAY_ITEMS))
    } else if (multiMode === 'online') {
      setListItems((list) => list.slice())
    } else {
      setListItems((list) => sortAlpha(list))
    }
    handleStartToggle()

    setGameState('active')
    selectedStartRetryRef.current = { attempts: 0, listId: undefined }
    if (mode === 'single') {
      setSoloGameKey((prev) => prev + 1)
    }
  }
  startGameRef.current = startGame

  // useEffect(() => {
  //   renderCanvas()
  // }, [])

  // const renderCanvas = () => {
  //   const c = document.getElementById('canvas') as HTMLCanvasElement
  //   const ctx = c.getContext('2d')
  //   if (!ctx) return
  //   const img = document.getElementById('pencilImg') as HTMLImageElement
  //   if (!img) return

  //   for (let i = 0; i < 100; i++) {
  //     ctx.drawImage(img, 0, 0, 512, 512, 10 * i, 10 * i, 10, 10)
  //   }
  // }

  const handlePollSelect = (poll: Poll) => {
    setDailyPollContext(undefined)
    setPollBackToRandom(false)
    pollBackOriginRef.current = 'polls'
    setPollPlayBackTarget('polls')
    pollState.handlePollSelect(poll)
  }

  const handlePollVote = (poll: Poll, shouldNavigate = true) => {
    setDailyPollContext(undefined)
    setPollBackToRandom(false)
    pollBackOriginRef.current = 'polls'
    setPollPlayBackTarget('polls')
    pollState.handlePollVote(poll, shouldNavigate)
  }

  const handlePollPlay = (poll: Poll) => {
    if (!requireLobby()) return
    if (poll.questionExperienceVersion === 2) playClick1()
    setDailyPollContext(undefined)
    setPollBackToRandom(false)
    pollBackOriginRef.current = 'polls'
    setPollPlayBackTarget('polls')
    pollState.handlePollPlay(poll)
  }

  const handlePollPlayFromDetail = (poll: Poll) => {
    if (!requireLobby()) return
    setDailyPollContext(undefined)
    setPollBackToRandom(false)
    pollBackOriginRef.current = 'polls'
    setPollPlayBackTarget('detail')
    pollState.handlePollPlay(poll)
  }

  const handlePollBack = () => {
    if (dailyPollContext) {
      setDailyPollContext(undefined)
      pollState.resetPollSelection()
      setViewMode('home')
      setSelectedTab('play')
      setSelectedListTab('daily')
      history.replace(homePath)
      return
    }
    setPollBackToRandom(false)
    pollBackOriginRef.current = 'polls'
    setPollPlayBackTarget('polls')
    pollState.handlePollBack()
  }

  const handleBackToPollDetail = () => {
    const pollId = pollState.selectedPollId
    if (!pollId) {
      handlePollBack()
      return
    }
    setViewMode('poll_detail')
    history.replace(`/polls/${pollId}`)
  }

  const isPrivateLobbyGuest =
    multiMode === 'online' &&
    onlineMatchType === 'lobby' &&
    !!gameCode &&
    !!hostId &&
    !!connectionId &&
    hostId !== connectionId

  const handleTitleBack = () => {
    if (
      multiMode === 'online' &&
      onlineMatchType === 'matchmaking' &&
      gameState === 'active'
    ) {
      handleLeaveMatchmakingSession()
      return
    }
    if (
      multiMode === 'online' &&
      onlineMatchType === 'matchmaking' &&
      gameState === 'over'
    ) {
      handleLeaveMatchmakingSession(true)
      return
    }
    if (viewMode === 'poll_play') {
      const pollId = pollState.selectedPollId
      setActivePollId(undefined)
      setGameState('pre')
      if (dailyPollContext) {
        setDailyPollContext(undefined)
        pollState.resetPollSelection()
        setViewMode('home')
        setSelectedTab('play')
        setSelectedListTab('daily')
        history.replace(homePath)
        return
      }
      setSelectedListTab('polls')
      if (pollPlayBackTarget === 'detail' && pollId) {
        setViewMode('poll_detail')
        history.replace(`/polls/${pollId}`)
      } else {
        pollState.handlePollBack()
      }
      return
    }
    if (isPrivateLobbyGuest) {
      handleLeaveLobby()
      return
    }
    handlePreScreen()
  }

  const cleanExperience = () => {
    if (gameCode) exitedLobbyCodesRef.current.add(gameCode)
    contentEpochRef.current += 1
    randomListRequestRef.current += 1
    questionStartRef.current.sequence += 1
    pendingStartItemsRef.current = null
    clearStaleLobbyState()
    pollState.resetPollSelection()
    setExperience(undefined)
    setMode(undefined)
    setMultiMode('local')
    setViewMode('home')
    setGameState('pre')
    setActivePollId(undefined)
    setListItems([])
    setTopic('')
    setSelectedList(undefined)
    setListRated(undefined)
    setLoading(false)
    setPollLoading(false)
    setSoloXpAwarded(undefined)
    setSelectedTab('play')
    setShowModeSelectOnly(true)
  }
  const leaveExperience = () => {
    if (gameCode) {
      const host = hostId === connectionId
      const message =
        onlineMatchType === 'matchmaking' && gameState !== 'over'
          ? 'Are you sure you want to leave? This may count as a ranked loss.'
          : host
            ? 'Are you sure you want to close the lobby?'
            : 'Are you sure you want to leave the lobby?'
      const needsConfirmation =
        onlineMatchType === 'matchmaking'
          ? gameState !== 'over'
          : !host || players.length > 1
      if (needsConfirmation && !window.confirm(message)) return false
      ws?.send(
        JSON.stringify({
          event: host && onlineMatchType !== 'matchmaking' ? 'closeLobby' : 'leaveLobby',
          data: { code: gameCode },
        })
      )
    } else if (pendingMatch) ws?.send(JSON.stringify({ event: 'cancelMatchSearch' }))
    cleanExperience()
    return true
  }
  exitExperienceRef.current = leaveExperience
  const goToModeSelectClean = () => {
    if (!leaveExperience()) return
    history.replace('/')
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  // A direct list route can retain the default Daily tab without being today's challenge.
  const isDailyChallengeGame =
    mode === 'single' && !activePollId && !!selectedList && selectedList === dailyList?.id
  const isMatchmakingGame =
    mode === 'multi' &&
    multiMode === 'online' &&
    onlineMatchType === 'matchmaking' &&
    gameState !== 'pre'
  const isPollContext =
    viewMode === 'poll_play' ||
    viewMode === 'poll_vote' ||
    viewMode === 'poll_detail' ||
    pollState.isPollDetailView ||
    !!activePollId
  const matchmakingLabel = onlineMatchRated
    ? `Ranked ${myMatchmakingStats?.elo ?? 0}`
    : 'Unranked'
  const newListLabel = isDailyChallengeGame
    ? 'Daily Challenge'
    : isMatchmakingGame
      ? matchmakingLabel
      : isPollContext && dailyPollContext === 'today'
        ? 'Daily Vote'
        : isPollContext && dailyPollContext === 'previous'
          ? 'Daily Poll'
          : isPollContext
            ? 'New Poll'
            : 'New List'

  const gameViewProps = {
    gameState,
    mode,
    multiMode,
    fullScreen,
    selectedList,
    activePollId,
    hostPlayMode,
    orderedListItems,
    listItems,
    setListItems,
    myPlayer,
    players,
    authoritativeOnlineAction,
    onlineVoteState,
    onlineMatchRated,
    onlineMatchTrophyDelta,
    multiplayerXpAwarded,
    onlineRematchState,
    isOnlineHost:
      multiMode === 'online' &&
      !!hostId &&
      (hostId === connectionId || hostId === String(Cookies.get('uuid') || '')),
    hostId,
    connectionId,
    ws,
    gameCode,
    handleGameOver,
    handleNewGame,
    handleHostPlayMode,
    dailyList,
    dailyResult,
    dailyProgress,
    loading: loading || pollLoading,
    listRated,
    appUserId: appState?.user?.id,
    user,
    doCreate,
    topic,
    sounds,
    handleEndGame,
    handleRestartGame,
    handlePlayAgainMatchmaking:
      MATCHMAKING_ENABLED && experience === 'online' && onlineMatchType !== 'lobby'
        ? handlePlayAgainMatchmaking
        : undefined,
    handleLeaveOnlineLobby: handleLeaveLobby,
    handleLeaveMatchmakingSession:
      multiMode === 'online' && onlineMatchType !== 'lobby'
        ? handleLeaveMatchmakingSession
        : undefined,
    handleCreateList,
    handleRateList,
    setRateAlert,
    historyPush: history.push,
    soloGameKey,
    handlePreScreen,
    setGameState,
    setDailyResult,
    onDailyProgressChange: handleDailyProgressChange,
    dailyPollContext,
    handlePlayedList,
    onHasGuessChange: (hasGuess: boolean) => setHasAnyGuess(hasGuess),
    soloStats,
    soloXpAwarded,
    onSoloStatsUpdate: (stats?: UserSoloStats) => setSoloStats(stats),
    onSoloXpAwarded: (xp?: number) => setSoloXpAwarded(xp),
    exitLabel:
      viewMode === 'poll_play'
        ? dailyPollContext
          ? 'Exit'
          : pollPlayBackTarget === 'detail'
            ? 'Poll'
            : 'Polls'
        : multiMode === 'online' && onlineMatchType === 'matchmaking'
          ? 'Leave'
          : multiMode === 'online' && onlineMatchType === 'lobby'
            ? isPrivateLobbyGuest
              ? 'Leave'
              : 'Lobby'
            : 'Exit',
    onExit: handleTitleBack,
    newListLabel,
    newListDisabled:
      isDailyChallengeGame ||
      isMatchmakingGame ||
      Boolean(dailyPollContext && isPollContext) ||
      loading ||
      pollLoading,
    onNewList: () => {
      if (isDailyChallengeGame) return
      if (dailyPollContext && isPollContext) return
      handleEndGame()
      if (activePollId || viewMode === 'poll_play' || viewMode === 'poll_vote') {
        handleNewPoll({ silentStart: true })
        return
      }
      if (mode === 'single') {
        handleStartRandomList()
        return
      }
      handleNewGame({ silentPollStart: true })
    },
  }

  const pollViewProps = {
    answerFocus: viewMode === 'poll_vote',
    isPollDetailView: pollState.isPollDetailView,
    selectedPollId: pollState.selectedPollId,
    pollLoading,
    userId: appState?.user?.id,
    activePollId,
    onPollDeleted: pollState.handlePollDeleted,
    onPlayPoll: handlePollPlayFromDetail,
    onPollBack: handlePollBack,
    onBackToDetail: handleBackToPollDetail,
    pollBackLabel: dailyPollContext
      ? 'Exit'
      : viewMode === 'poll_vote' && pollPlayBackTarget === 'detail'
        ? 'Poll'
        : 'Polls',
    onNewPoll: () => handleNewPoll({ silentStart: true }),
    topRightLabel:
      dailyPollContext === 'today'
        ? 'Daily Poll'
        : dailyPollContext
          ? 'Daily Poll'
          : undefined,
    onOpenHowToPlay: () => {
      playClick1()
      setHowToPlayOpen(true)
    },
    sounds: {
      click: sounds.click,
      submit: sounds.clickAlt,
      drag: sounds.swipe,
      celebrate: sounds.celebrate,
    },
  }

  return (
    <DashboardShell
      rateAlert={rateAlert}
      listAlert={listAlert}
      onCloseRateAlert={() => setRateAlert({ open: false })}
      onCloseListAlert={() => setListAlert({ open: false })}
      mode={mode}
      fullScreen={fullScreen}
      gameState={gameState}
      musicIsPlaying={musicIsPlaying}
      volumeExpanded={volumeExpanded}
      volumeLevel={volumeLevel}
      appUserId={appState?.user?.id}
      avatarMenuAnchorEl={avatarMenuAnchorEl}
      avatarMenuOpen={open}
      onTitleClick={handleTitleBack}
      onToggleMusic={() => handleMusicToggle()}
      onExpandVolume={(expanded) => setVolumeExpanded(expanded)}
      onVolumeChange={(value) => setVolumeLevel(Math.min(value, 100))}
      onAvatarMenuClick={handleAvatarMenuClick}
      onAvatarMenuClose={handleAvatarMenuClose}
      onLogin={() => history.push('/login')}
      onAccount={() => history.push('/account')}
      showAdminOption={isAdminUser}
      onAdmin={() => history.push('/admin')}
      onLogout={() => void handleLogout()}
      showEmojiPreview={showEmojiPreview}
      showHowToPlay={howToPlayOpen}
      // selectedPlayer={selectedPlayer}
      onCloseEmoji={() => handleCloseEmojiPreview()}
      onCloseHowToPlay={() => setHowToPlayOpen(false)}
      onChangeIcon={function (playerId: number | undefined, emoji: string): void {
        console.log('Function not implemented.')
      }}
      sounds={{ click: sounds.click, swipe: sounds.swipe }}
      // onChangeIcon={(playerId, emoji) => {
      //   changePlayerIcon(playerId, emoji)
      // }}
    >
      <DashboardContentView
        booted={booted}
        viewKey={viewKey}
        viewVariants={viewVariants}
        gameState={gameState}
        viewMode={viewMode}
        isPollDetailView={pollState.isPollDetailView}
        isPollPlayView={viewMode === 'poll_play'}
        selectedTab={selectedTab}
        showModeSelectOnly={showModeSelectOnly}
        mode={mode}
        fullScreen={fullScreen}
        multiMode={multiMode}
        selectedListTab={selectedListTab}
        sounds={sounds}
        handleSelectTab={handleSelectTab}
        setSelectedTab={setSelectedTab}
        setSelectedList={setSelectedList}
        setActivePollId={setActivePollId}
        experience={experience}
        onSelectExperience={handleSelectExperience}
        dailyList={dailyList}
        soloStats={soloStats}
        onStartGame={handleStartGame}
        user={user}
        pollRefreshKey={pollState.pollRefreshKey}
        onPollSelect={handlePollSelect}
        onPollVote={handlePollVote}
        onPollPlay={handlePollPlay}
        canCreatePoll={canCreatePoll}
        setTopic={setTopic}
        setListItems={setListItems}
        onStartRandomList={handleStartRandomList}
        onStartRandomPoll={handleStartRandomPoll}
        onStartDailyPoll={() => handleStartDailyPoll('today')}
        onStartPreviousPoll={() => handleStartDailyPoll('previous')}
        onStartCreatedList={handleStartCreatedList}
        onAlert={setListAlert}
        appUserId={appState?.user?.id}
        onEditList={handleEditList}
        editList={editListDraft}
        listSearchShowMine={listSearchShowMine}
        onListSearchShowMine={setListSearchShowMine}
        onBackFromListEdit={handleBackFromListEdit}
        handleMakeMatch={handleMakeMatch}
        handleCreateLobby={handleCreateLobby}
        handleJoinLobby={handleJoinLobby}
        onChangeJoinCode={(value) => setJoinGameCode(sanitizeLobbyCode(value))}
        onlineProfileName={onlineProfileName}
        onlineProfileIcon={onlineProfileIcon}
        canEditOnlineName={!appState?.user?.id}
        onChangeOnlineName={handleChangeOnlineName}
        players={players}
        connectionId={connectionId}
        hostId={hostId}
        onChangeOnlineTeam={handleChangeOnlineTeam}
        onChangeOnlineIcon={handleChangeOnlineIcon}
        onCommitOnlineName={handleCommitOnlineName}
        onStartOnlineLobbyGame={handleStartOnlineLobbyGame}
        onCloseLobby={handleCloseLobby}
        onLeaveLobby={handleLeaveLobby}
        onlineUsersAll={onlineUsersAll}
        onlineUsersLogged={onlineUsersLogged}
        searchingRanked={searchingRanked}
        searchingUnranked={searchingUnranked}
        matchmakingRated={matchmakingRated}
        onSetMatchmakingRated={setMatchmakingRated}
        joinGameCode={joinGameCode}
        leaderboard={matchmakingLeaderboard}
        myStats={myMatchmakingStats}
        onlineMatchRated={onlineMatchRated}
        onlineMatchType={onlineMatchType}
        pendingMatch={pendingMatch}
        gameCode={gameCode}
        ws={ws}
        onSetSelectedListTab={setSelectedListTab}
        onPollCreated={pollState.handlePollCreated}
        gameViewProps={gameViewProps}
        pollViewProps={pollViewProps}
        onSelectModeClick={() => {
          playClick1()
          goToModeSelectClean()
        }}
        onOpenHowToPlay={() => {
          playClick1()
          setHowToPlayOpen(true)
        }}
        onPollBack={handlePollBack}
        onNewPoll={() => handleNewPoll({ silentStart: true })}
      />
    </DashboardShell>
  )
}
